import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withGuestPrices } from '../../lib/taxMath';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Utensils, Search, ArrowLeft, AlertCircle, Plus, Minus, Trash2,
  CheckCircle2, Armchair, X, ChevronDown, User, Clock, Ban,
  BookOpen, ConciergeBell, Receipt, LogOut, Phone, Star, Eye, Users, MapPin,
} from 'lucide-react';
import { formatCurrencyWith } from '../../utils/dateFormat';
import { customerAuth } from '../../services/customerService';
import { confirm, alertDialog } from '../../components/ui/confirm-dialog';
import { subscribeRealtime } from '../../lib/realtime';
import { useT } from '../../context/LanguageContext';
import OrderPlacedCelebration from '../../components/customer/OrderPlacedCelebration';
import WalletQrScanner from '../../components/customer/WalletQrScanner';
import JoinRewards from '../../components/customer/JoinRewards';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// "HH:MM" (24h) → "h:MM AM/PM". Returns the input unchanged if unparsable.
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const period = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${period}`;
};

/* The Mauritian wallets on the payment sheet, by the name they are known by —
   brands, so the same in both languages. Used for the "scan the counter's …
   QR" prompt; the chooser's own buttons carry the emoji labels. */
const WALLET_NAMES = { juice: 'Juice', myt: 'my.t money' };

// Brand accent (SOCIAL-style orange), used for the CTA + highlights.
const ORANGE = '#EA6A12';
// Dark cocoa used for the header and the order bar (DotPe/Zomato-style shell).
const MAROON = '#3E1608';

/* ---------------------------------------------------------------- kiosk ---
 * THE SAME PAGE, STANDING UP. A self-order kiosk shows this exact screen —
 * same dishes, same cart, same pay-first rules — on a panel that is a metre
 * tall instead of a phone in a hand, so the only thing that changes is the
 * shape: the frame stops pretending to be a 440px handset and gives its left
 * edge to a standing rail of the menu's sections.
 *
 * Two triggers, one layout. The kiosk shell appends `kiosk=1` to the branch's
 * self-order link (windows_pos/electron/main.js), which is deterministic — a
 * panel's CSS width depends on its Windows scaling and cannot be trusted —
 * and any wide BROWSER gets the same thing responsively at `lg`. Read once at
 * module load: a kiosk never navigates away from its own URL.
 *
 * The classes are spelled out in full on both branches rather than assembled
 * from a prefix, because Tailwind only ships the class names it can SEE in
 * the source. */
const IS_KIOSK = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('kiosk') === '1';
const FRAME_WIDE = IS_KIOSK ? 'max-w-none pl-[268px]' : 'lg:max-w-none lg:pl-[268px]';
const RAIL_SHOW = IS_KIOSK ? 'flex' : 'hidden lg:flex';
const NAV_WIDE = IS_KIOSK
  ? 'left-[268px] translate-x-0 max-w-none w-[calc(100%-268px)]'
  : 'lg:left-[268px] lg:translate-x-0 lg:max-w-none lg:w-[calc(100%-268px)]';

/* A phone face down on the table has to be NOTICED when the pass calls a dish,
   so the "ready" dialog arrives with a buzz and a chirp. Both are best-effort:
   a handset with no vibration motor, or a browser holding sound back until it
   sees a tap, simply gets the dialog on its own. */
function buzzGuest() {
  try { navigator.vibrate?.([180, 90, 180]); } catch { /* unsupported */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    // Two notes, service-bell style — a single tone reads as an error sound.
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.16);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.onended = () => { try { ctx.close(); } catch { /* already closed */ } };
    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  } catch { /* autoplay policy — the dialog carries the message by itself */ }
}

// Dish spice level ('' = not spicy): label KEY + chili count for the badge.
// These maps live outside the component, so they hold translation keys and the
// wording is looked up at render — a hook can never be called out here.
const SPICY_LABELS = {
  less: 'guest.menu.spicy.less',
  normal: 'guest.menu.spicy.normal',
  extra: 'guest.menu.spicy.extra',
};
const SPICY_PEPPERS = { less: 1, normal: 2, extra: 3 };

// Live status line on the Orders tab, keyed by the backend's statuses; DELIVERED is the model's "served".
const ORDER_STATUS_META = {
  PENDING: { key: 'guest.menu.orderStatus.sent', cls: 'text-emerald-700' },
  ORDERED: { key: 'guest.menu.orderStatus.sent', cls: 'text-emerald-700' },
  CONFIRMED: { key: 'guest.menu.orderStatus.confirmed', cls: 'text-emerald-700' },
  PREPARING: { key: 'guest.menu.orderStatus.preparing', cls: 'text-amber-600' },
  /* Ready reads two ways. At a counter nobody is walking the food over, so
     "coming to your table" is simply untrue — collectKey is the wording used
     where the guest fetches it themselves. */
  READY: {
    key: 'guest.menu.orderStatus.ready',
    collectKey: 'guest.menu.orderStatus.readyCollect',
    cls: 'text-sky-600',
  },
  DELIVERED: { key: 'guest.menu.orderStatus.delivered', cls: 'text-emerald-700 font-medium' },
  COMPLETED: { key: 'guest.menu.orderStatus.completed', cls: 'text-emerald-700 font-medium' },
  CANCELLED: { key: 'guest.menu.orderStatus.cancelled', cls: 'text-red-600' },
};

// Per-DISH status on the Orders tab: one order's dishes move through the
// kitchen one by one, so each line reports its own state — the kebab can be
// on the table while the chicken is still on the fire.
const ITEM_STATUS_META = {
  HELD: { key: 'guest.menu.itemStatus.held', cls: 'text-gray-400' },
  PENDING: { key: 'guest.menu.itemStatus.sent', cls: 'text-gray-500' },
  PREPARING: { key: 'guest.menu.itemStatus.cooking', cls: 'text-amber-600' },
  READY: {
    key: 'guest.menu.itemStatus.ready',
    collectKey: 'guest.menu.itemStatus.readyCollect',
    cls: 'text-sky-600',
  },
  SERVED: { key: 'guest.menu.itemStatus.served', cls: 'text-emerald-700' },
};
// `t` is passed in: this is a plain helper, not a component, so it cannot hold
// a hook; `collect` says the guest fetches their own food (counter, no table).
const itemStatusLine = (l, t, collect = false) => {
  const meta = ITEM_STATUS_META[l.status];
  if (!meta) return null; // local snapshot before the first server refresh
  let text = t(collect && meta.collectKey ? meta.collectKey : meta.key);
  if (l.status === 'SERVED' && l.served_at) {
    const d = new Date(l.served_at);
    if (!Number.isNaN(d.getTime())) {
      text = t('guest.menu.itemStatus.servedAt', {
        time: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      });
    }
  }
  return { text, cls: meta.cls };
};

/* Language switch for the guest page. The QR menu carries no app chrome, so a
   French-speaking diner has nowhere else to change it; a two-state EN|FR pill
   is findable without already reading English. Declared at module level — a
   component defined inside CustomerMenu remounts on every poll (4s) and eats
   the tap on a phone. */
function GuestLanguageSwitch() {
  const { locale, setLocale, locales } = useT();
  return (
    <div
      className="shrink-0 inline-flex items-center rounded-full border border-white/30 overflow-hidden"
      role="group"
      aria-label="Language / Langue"
    >
      {Object.keys(locales).map((code) => {
        const active = code === locale;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLocale(code)}
            aria-pressed={active}
            /* The native name, so the choice reads in the language it selects. */
            title={locales[code].nativeLabel}
            className={`px-2.5 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${
              active ? 'text-white' : 'text-white/60'
            }`}
            style={active ? { background: ORANGE } : {}}
          >
            {locales[code].flag}
          </button>
        );
      })}
    </div>
  );
}

/* The buffet counter panel for a guest at a buffet table: a message to the kitchen, never an order. */
function BuffetCounterPanel({ token, spread, onAsk, asking, pending }) {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  if (!token || !spread?.buffet) return null;
  const sections = spread.sections || [];
  const waiting = (pending || []).filter(
    (r) => r.status === 'NEW' || r.status === 'PREPARING');

  return (
    <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <ConciergeBell className="h-5 w-5 shrink-0" style={{ color: ORANGE }} />
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-semibold text-gray-900">
            {spread.buffet.name}
          </span>
          <span className="block text-[12px] text-gray-500">
            {t('guest.menu.buffet.askLine')}
          </span>
        </span>
        {waiting.length > 0 && (
          <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white"
            style={{ background: ORANGE }}>
            {t('guest.menu.buffet.onTheWay', { count: waiting.length })}
          </span>
        )}
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* What has already been asked for, so nobody taps the same dish four times waiting for it. */}
      {waiting.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {waiting.map((r) => (
            <span key={r.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {r.qty > 1 ? `${r.qty}× ` : ''}{r.item}
              <span className="text-emerald-600/70">
                {r.status === 'PREPARING'
                  ? `· ${t('guest.menu.buffet.inProgress')}`
                  : `· ${t('guest.menu.buffet.sent')}`}
              </span>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="border-t border-gray-100">
          {sections.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">
              {t('guest.menu.buffet.noSpread')}
            </p>
          ) : sections.map((sec) => (
            <div key={sec.name} className="px-4 py-3 border-b border-gray-50 last:border-0">
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">
                {sec.name}
              </div>
              <div className="flex flex-wrap gap-2">
                {sec.items.map((item) => {
                  const already = waiting.some(
                    (r) => r.item.toLowerCase() === item.toLowerCase());
                  return (
                    <button
                      key={item}
                      type="button"
                      disabled={already || asking === item}
                      onClick={() => onAsk(item)}
                      className={`rounded-xl border px-3 py-2 text-[13px] font-medium ${
                        already
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-white text-gray-700 active:bg-gray-50'
                      }`}
                    >
                      {item}
                      {already ? ' ✓' : asking === item ? ' …' : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** CustomerMenu: the public ordering page after login - home tiles, browse view, cart bar and tabs. */
export default function CustomerMenu() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const token =
    params.get('token') || sessionStorage.getItem('customerToken') || '';
  const directRestaurant = params.get('restaurant');
  const directMenu = params.get('menu');
  /* Counter/kiosk entry: the QR names a BRANCH and no table at all, so the
     menu loads against the branch and the order goes to its online counter. */
  const directLocation = params.get('location');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menu, setMenu] = useState(null);
  // Several menus can be live at once, so the guest picks which to browse and can switch from the tabs.
  const [menuChoices, setMenuChoices] = useState([]);
  const [chosenMenuId, setChosenMenuId] = useState(null);
  const [context, setContext] = useState(null);
  /* Pay-first follows the TABLE, not a branch switch. A guest with no seat —
     a kiosk, or this site opened from the menu QR — has nothing to hang a
     running bill on, so payment (mock, for now) runs BEFORE the order is sent,
     the order arrives at the POS already settled, there is no Pay Bill tab,
     and every confirmed cart is its own order with a fresh number. A guest at
     a table is billed at the end, their rounds joining one order. Declared
     after `session` below, which is where the table is known. */
  const [search, setSearch] = useState('');
  // SOCIAL-style navigation: home (category tiles) vs browse (dish list).
  const [view, setView] = useState('home');
  // Bottom nav: menu | orders | bill
  const [tab, setTab] = useState('menu');
  // Active section in browse view ('all' or a section id).
  const [activeSection, setActiveSection] = useState('all');
  // Active dish-category chip within the section ('all' or a category name).
  const [activeCat, setActiveCat] = useState('all');

  // Table session (from the QR login or the on-screen table picker).
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('customerSession') || 'null');
    } catch {
      return null;
    }
  });
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableError, setTableError] = useState('');

  /* The buffet running at this branch now, plus this table's open asks; null for an a-la-carte table. */
  const [buffetSpread, setBuffetSpread] = useState(null);
  const [askingItem, setAskingItem] = useState('');

  // Cart + ordering.
  /* The cart outlives this page. Signing in mid-build is a NAVIGATION away and
     back — a guest who chose four dishes at the counter must not return to an
     empty cart — and a dropped connection or a stray back-tap costs nothing
     either. Per tab, like the rest of the sitting's cache. */
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('customerCart') || '[]');
    } catch {
      return [];
    }
  });
  // Dish descriptions are clamped to one line; tapping toggles the full text.
  const [expandedDesc, setExpandedDesc] = useState(() => new Set());
  const toggleDesc = (id) =>
    setExpandedDesc((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const [cartOpen, setCartOpen] = useState(false);
  const [picker, setPicker] = useState(null); // {item, variantId, addons: {addon_id: qty}}
  const [placing, setPlacing] = useState(false);
  const [orderError, setOrderError] = useState('');
  // Optional free-text note for the kitchen, sent with the order.
  const [orderNote, setOrderNote] = useState('');
  const [placedOrder, setPlacedOrder] = useState(null);
  /* The full-screen "Order placed successfully" moment, shown once a PAID
     (self-service) order has actually reached the kitchen. It holds a frozen
     copy of what was sent, because the cart is emptied the same instant. */
  const [celebration, setCelebration] = useState(null);
  // Every order placed this sitting — feeds the Orders tab and Pay Bill.
  const [placedOrders, setPlacedOrders] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('customerPlacedOrders') || '[]');
    } catch {
      return [];
    }
  });

  // The sitting's order number: every further round lands on this same order until the bill is paid.
  const [activeOrderNumber, setActiveOrderNumber] = useState(
    () => sessionStorage.getItem('customerActiveOrder') || ''
  );

  // Live order statuses keyed by order number, kept fresh for the whole sitting.
  const [orderStatuses, setOrderStatuses] = useState({});
  /* The sheet that interrupts the guest, for the two facts they must not miss:
     food plated, or an order pulled. { kind:'ready'|'cancelled', … } or null. */
  const [notice, setNotice] = useState(null);
  // Every order line's last-seen kitchen state, keyed `<order>#<index>#<name>`.
  // null until the first fetch seeds it, so arriving at an order whose dishes
  // are ALREADY plated never announces food the guest has long since been told
  // about — only a state that changes under this phone's eyes is news.
  const seenItemStateRef = useRef(null);
  // The same, for the ORDERS themselves — a cancellation is an order-level fact.
  const seenOrderStateRef = useRef(null);

  // The party-size prompt and table reservation, validated against the table's capacity by the backend.
  const [paxModal, setPaxModal] = useState(false);
  const [paxValue, setPaxValue] = useState(2);
  const [paxError, setPaxError] = useState('');
  const [paxSaving, setPaxSaving] = useState(false);
  // Live sharing status: true when the table hosts two or more open parties, kept fresh by polling.
  const [sharedNow, setSharedNow] = useState(false);
  // Set when the table is already occupied by another guest — blocks ordering.
  const [tableBlocked, setTableBlocked] = useState('');
  // Restaurant phone returned with the 409 — powers the "Call waiter" button.
  const [blockedPhone, setBlockedPhone] = useState('');
  // The guest's own open sitting on the server (undefined = still asking, null = none). The server is the source
  // of truth: a fresh tab, a re-scan or another phone picks the sitting up from here instead of asking the party
  // size again — the per-tab storage below is only a cache of it.
  const [mySitting, setMySitting] = useState(undefined);
  // Set when this page opened on ANOTHER table's QR while the guest's sitting is still open elsewhere: a guest
  // sits at one table at a time, so ordering here is blocked and they are sent back to their own table.
  const [seatedElsewhere, setSeatedElsewhere] = useState(null);
  // The sitting already adopted on this page, so a menu switch never re-applies the older snapshot over live polls.
  const adoptedSittingRef = useRef(null);

  // The menu needs a signed-in guest: accept an account login or the QR table token, else bounce to login.
  const isCustomerAuthed =
    customerAuth.isLoggedIn() || !!sessionStorage.getItem('customerToken');
  // "Skip - just browse" views the menu without signing in; ordering still bounces back to the login.
  const browseOnly = params.get('browse') === '1' && !isCustomerAuthed;
  // Pre-order mode from the reservation page: the cart is saved onto the reservation and fired at check-in.
  const preorderId = params.get('preorder') || '';
  /* NO SEAT is the whole test, and it decides both facts at once.

     A guest with no table pays as they order (nothing to hang a running bill
     on) and their order stands alone on the branch's ONLINE counter; a guest
     at a table is billed at the end and their rounds join one order. This used
     to be two per-branch switches that could contradict each other — kiosk
     mode on with pay-after-eating left a printed QR nobody could order from —
     and neither switch exists any more. Ordering without a table is never
     gated: a kiosk order and a phone order both always reach the board. */
  const isSelfService = !session?.table_id;
  const canOrderWithoutTable = true;
  /* Nobody is bringing the food over: a self-service counter hands it across,
     and a guest with no table has no seat for it to arrive at. Everywhere the
     app would otherwise promise a delivery to the table, it says "collect"
     instead — a promise the room cannot keep is worse than no promise. */
  const collectAtCounter = !session?.table_id;
  /* A KIOSK QR opened with "just browse" is NOT window-shopping: the guest is
     standing at the counter with the code in front of them. So the menu keeps
     its ordering controls and offers the sign-in that turns reading into
     ordering — unlike the website's read-only browse, which has no counter to
     order at and therefore nothing to sign in for. */
  const kioskBrowse = browseOnly && !!directLocation && canOrderWithoutTable;
  // Ordering controls come off only where there is genuinely nothing to order from.
  const readOnlyMenu = browseOnly && !kioskBrowse;
  useEffect(() => {
    if (!isCustomerAuthed && !browseOnly) {
      const nextUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`/customer/account-login?next=${nextUrl}`, { replace: true });
    }
  }, [isCustomerAuthed, browseOnly, navigate, location.pathname, location.search]);

  // Bounce a browse-only guest to the login and return here without the browse flag, so ordering unlocks.
  const promptLogin = () => {
    const p = new URLSearchParams(location.search);
    p.delete('browse');
    const q = p.toString();
    const nextUrl = encodeURIComponent(location.pathname + (q ? `?${q}` : ''));
    navigate(`/customer/account-login?next=${nextUrl}`);
  };

  useEffect(() => {
    if (!isCustomerAuthed && !browseOnly) return;
    const hasDirect = !!directRestaurant && !!(directMenu || directLocation);
    if (!token && !hasDirect) {
      // A translation KEY, not a sentence: this effect must not depend on the
      // language, so the wording is resolved where the banner renders (a
      // server-sent message passes straight through the same lookup).
      setError('guest.menu.error.missingToken');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const menuQuery = chosenMenuId ? `?menu_id=${chosenMenuId}` : '';
        let url;
        if (directRestaurant && directMenu) {
          // Menu QR: one named menu, opened straight from a poster or flyer.
          url = `${API_URL}/users/public/restaurant/${directRestaurant}/menu/${directMenu}/`;
        } else if (directRestaurant && directLocation) {
          // Customer Self Service QR: the branch's live menus, no table and no token to validate.
          /* A kiosk says so, and is answered with the menus ticked for it
             (Menu editor -> Kiosk). A guest's own phone on the same counter
             QR is not a kiosk and still gets the full list. */
          url = `${API_URL}/users/public/restaurant/${directRestaurant}/location/${directLocation}/menu/${menuQuery}`;
          if (IS_KIOSK) url += `${menuQuery ? '&' : '?'}kiosk=1`;
        } else {
          url = `${API_URL}/users/customer/menu/${encodeURIComponent(token)}/${menuQuery}`;
        }
        const res = await fetch(url);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || 'guest.menu.error.loadMenu');
        } else if (data.menus && !data.data) {
          // More than one menu is live, so let the guest pick which to browse; a single active menu opens directly.
          setMenuChoices(data.menus);
          setContext(data.context);
        } else {
          // Prices as the guest pays them, the net beside each for the order line.
          setMenu(withGuestPrices(data.data));
          setContext(data.context);
        }
      } catch (err) {
        console.error(err);
        setError('guest.menu.error.network');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, directRestaurant, directMenu, directLocation, isCustomerAuthed, browseOnly, chosenMenuId]);

  /* ONE socket for the whole visit, opened the moment the guest has a token —
     on the MENU, not only once they have an order to watch. Everything live on
     this page rides it (order moves, the table being shared, the buffet
     counter), and because the client pools connections by URL every subscriber
     below joins this same socket rather than opening its own.

     `socketLive` is the honest answer to "is push working here": hotel wifi,
     a corporate proxy or a plain old captive portal all kill WebSockets, and
     on those phones the polls below are the only thing keeping the screen
     true. So the polls never stop — they just ease off to a safety net while
     the socket is proven up, which is the difference between a one-second
     update and a 45-second one, and a good deal less of the guest's data. */
  const [socketLive, setSocketLive] = useState(false);
  useEffect(() => {
    const tk = customerAuth.token();
    if (!tk) { setSocketLive(false); return undefined; }
    const unsub = subscribeRealtime(
      ['connected', 'disconnected', 'unavailable', 'refused'],
      (msg) => setSocketLive(msg.kind === 'connected'),
      { token: tk, app: 'customer' },
    );
    return () => { unsub(); setSocketLive(false); };
  }, [isCustomerAuthed]);

  /* Is a buffet on the counter, and what has this table asked for? Re-read after every ask and on a slow poll. */
  const loadBuffetSpread = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `${API_URL}/users/customer/buffet/${encodeURIComponent(token)}/`);
      const data = await res.json();
      setBuffetSpread(data?.success ? data.data : null);
    } catch {
      // No buffet, an older server or no network: the panel stays hidden rather than showing a useless error.
      setBuffetSpread(null);
    }
  }, [token]);

  useEffect(() => {
    loadBuffetSpread();
    /* The counter changes when the kitchen answers an ask, so the push is what
       makes it feel instant; the interval drops to a safety net once the
       socket is proven up. */
    const unsub = subscribeRealtime(['buffet', 'buffet_request', 'buffet_refill'],
      () => loadBuffetSpread(),
      { token: customerAuth.token(), app: 'customer' });
    const id = setInterval(loadBuffetSpread, socketLive ? 180000 : 45000);
    return () => { unsub(); clearInterval(id); };
  }, [loadBuffetSpread, socketLive]);

  const askForDish = async (item) => {
    setAskingItem(item);
    try {
      const res = await fetch(
        `${API_URL}/users/customer/buffet/${encodeURIComponent(token)}/request/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            item,
            guest_name: session?.customer_name || '',
          }),
        });
      const data = await res.json();
      if (!res.ok || !data.success) {
        await alertDialog({
          title: t('guest.menu.buffet.askFailedTitle'),
          description: data?.error || t('guest.menu.askStaff'),
        });
      }
      await loadBuffetSpread();
    } catch {
      await alertDialog({
        title: t('guest.menu.noConnection'),
        description: t('guest.menu.buffet.kitchenUnreachable'),
      });
    } finally {
      setAskingItem('');
    }
  };

  const pickMenu = (id) => {
    setSearch('');
    setActiveSection('all');
    setChosenMenuId(id);
  };

  // Menu-QR path with no table session yet: offer the table picker so the customer can start ordering.
  const needsTable = !session?.table_id;
  useEffect(() => {
    const locId = context?.location_id;
    if (!needsTable || !locId) return;
    setTablesLoading(true);
    fetch(`${API_URL}/customers/locations/${locId}/tables/`)
      .then((r) => r.json())
      .then((d) => setTables((d.data || []).filter((t) => t.is_active !== false)))
      .catch(() => setTables([]))
      .finally(() => setTablesLoading(false));
  }, [needsTable, context?.location_id]);

  /* The kitchen's "ready" is the one thing a diner wants pushed at them, so
     every refresh of the sitting's orders is diffed against what this phone
     last saw and the newly plated dishes raise the dialog. Diffing (rather than
     reading the push's payload) means the alert works the same whether the news
     came over the socket or over the fallback poll. */
  const announceChanges = useCallback((details) => {
    const prev = seenItemStateRef.current;
    const prevOrders = seenOrderStateRef.current;
    const next = new Map();
    const nextOrders = new Map();
    const dishes = [];
    let orderNumber = '';
    let whole = false;
    let cancelled = '';
    Object.entries(details || {}).forEach(([num, o]) => {
      const lines = (o && o.items) || [];
      let landed = false;
      const wasOrder = prevOrders ? prevOrders.get(num) : undefined;
      nextOrders.set(num, (o && o.status) || '');
      // Cancelled under the guest's nose — never on the first look, which only seeds.
      if (o.status === 'CANCELLED' && wasOrder !== undefined && wasOrder !== 'CANCELLED'
          && !cancelled) {
        cancelled = num;
      }
      lines.forEach((l, i) => {
        const key = `${num}#${i}#${l.name || ''}`;
        next.set(key, l.status || '');
        // A cancelled order's lines keep whatever state they died in - never announce those.
        if (o.status === 'CANCELLED') return;
        const was = prev ? prev.get(key) : undefined;
        if (l.status === 'READY' && was !== undefined && was !== 'READY') {
          dishes.push(l.name || '');
          landed = true;
        }
      });
      if (!landed) return;
      if (!orderNumber) orderNumber = num;
      // Every fired dish now plated = the whole ORDER is up, which is the
      // bigger headline; held (uncooked) courses stay out of the arithmetic.
      const fired = lines.filter((l) => l.status && l.status !== 'HELD');
      if (fired.length && fired.every((l) => l.status === 'READY' || l.status === 'SERVED')) {
        whole = true;
      }
    });
    seenItemStateRef.current = next;
    seenOrderStateRef.current = nextOrders;
    /* A pulled order is the bigger news and lands on its own: a dish that went
       ready moments before it is no longer something to tell anybody about. */
    if (cancelled) {
      setNotice({ kind: 'cancelled', orderNumber: cancelled });
      buzzGuest();
      return;
    }
    if (dishes.length === 0) return;
    setNotice({ kind: 'ready', dishes, orderNumber, whole });
    buzzGuest();
  }, []);

  // The sitting's orders are refreshed for the WHOLE visit, not just while the
  // Orders tab is open: "your dish is ready" has to reach a guest who is still
  // reading the menu. The socket carries it within a second of the pass pressing
  // Ready; the poll is the fallback, and it eases off away from the order list.
  const placedOrderNums = [
    ...new Set(placedOrders.map((o) => o.order_number).filter(Boolean)),
  ].join(',');
  const watchingOrders = tab === 'orders' || tab === 'bill';
  useEffect(() => {
    if (!placedOrderNums) return undefined;
    const load = () => {
      fetch(`${API_URL}/users/orders/status/?numbers=${encodeURIComponent(placedOrderNums)}&details=1`)
        .then((r) => r.json())
        .then((d) => {
          if (!d || !d.success || !d.data) return;
          const details = d.data;
          const statuses = {};
          Object.entries(details).forEach(([num, o]) => { statuses[num] = o.status; });
          setOrderStatuses(statuses);
          announceChanges(details);
          setPlacedOrders((prev) => {
            const seen = new Set();
            const next = prev
              .filter((p) => {
                if (!p.order_number || seen.has(p.order_number)) return false;
                seen.add(p.order_number);
                return true;
              })
              .map((p) => (details[p.order_number]
                ? { ...p, ...details[p.order_number] }
                : p));
            sessionStorage.setItem('customerPlacedOrders', JSON.stringify(next));
            return next;
          });
        })
        .catch(() => {});
    };
    load();
    // Realtime pushes a refresh to the guest's phone when staff move the order; polling stays as a fallback.
    const unsub = subscribeRealtime(['order'], () => load(),
      { token: customerAuth.token(), app: 'customer' });
    /* With push proven up the poll is only a safety net, so it steps right
       back — the socket already answers within a second of the pass pressing
       Ready, and a phone on mobile data should not be asked every 20s as well. */
    const timer = setInterval(
      load,
      socketLive ? 120000 : (watchingOrders ? 20000 : 45000),
    );
    // A phone that slept through the push catches up the moment it is picked up.
    const onWake = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onWake);
    return () => {
      unsub();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [watchingOrders, placedOrderNums, announceChanges, socketLive]);

  const selectTable = async (table, share = false) => {
    setTableError('');
    const profile = customerAuth.profile() || {};
    const name =
      [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
      profile.name || 'Guest';
    try {
      const res = await fetch(`${API_URL}/users/customer/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_id: context?.restaurant_id,
          table_id: table.id,
          name,
          phone: profile.phone || '',
          ...(share ? { share: true } : {}),
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.seated_at) {
        // The guest's own sitting is still open at another table - one table at a time, so send them back to it.
        setSeatedElsewhere(data.seated_at);
        setBlockedPhone(isSelfService ? '' : data.restaurant_phone || '');
        setShowTablePicker(false);
        return;
      }
      if (res.status === 409 && !share && data.can_share) {
        // Table has another party but seats remain — offer to share it.
        const seatNote =
          data.seats_left != null
            ? ` ${t(data.seats_left === 1
              ? 'guest.menu.table.seatsLeftOne'
              : 'guest.menu.table.seatsLeftMany', { count: data.seats_left })}`
            : '';
        const ok = await confirm({
          title: t('guest.menu.table.occupiedTitle', { code: table.table_code }),
          description: t('guest.menu.table.shareBody', { seats: seatNote }),
          confirmText: t('guest.menu.table.shareConfirm'),
          cancelText: t('guest.menu.table.pickAnother'),
        });
        if (ok) await selectTable(table, true);
        return;
      }
      if (!res.ok || !data.success) {
        setTableError(data.error || t('guest.menu.table.startFailed'));
        return;
      }
      sessionStorage.setItem('customerToken', data.data.token || '');
      sessionStorage.setItem('customerSession', JSON.stringify(data.data));
      setSession(data.data);
      setShowTablePicker(false);
    } catch (err) {
      console.error(err);
      setTableError(t('guest.menu.table.networkError'));
    }
  };

  // Leave the table: closes the open session so it frees up at once, using the same endpoint staff use.
  const leaveTable = async () => {
    if (!session?.session_id) return;
    // With orders placed, skip the confirm - the backend refuses the close and the message below explains why.
    if (placedOrders.length === 0) {
      const ok = await confirm({
        title: t('guest.menu.leave.title', {
          code: session.table_code || tableCode || '',
        }).replace('  ', ' '),
        description: t('guest.menu.leave.body'),
        confirmText: t('guest.menu.leave.confirm'),
        cancelText: t('guest.menu.leave.stayShort'),
        tone: 'destructive',
      });
      if (!ok) return;
    }
    try {
      const rid = session.restaurant_id || context?.restaurant_id;
      const res = await fetch(
        `${API_URL}/users/restaurant/${rid}/sessions/${session.session_id}/close/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Marks this as a guest-initiated close, which the backend refuses while an order is still being prepared.
          body: JSON.stringify({ guest: true }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        const phone = isSelfService ? '' : data.restaurant_phone || '';
        const onTheWay = data.reason !== 'bill_unsettled';
        const callWaiter = await confirm({
          title: onTheWay
            ? t('guest.menu.leave.onTheWayTitle')
            : t('guest.menu.leave.settleTitle'),
          description: onTheWay
            ? t('guest.menu.leave.onTheWayBody')
            : t('guest.menu.leave.settleBody'),
          confirmText: phone ? t('guest.menu.callWaiter') : t('guest.menu.ok'),
          cancelText: t('guest.menu.leave.stayAtTable'),
        });
        if (callWaiter && phone) window.location.href = `tel:${phone}`;
        return;
      }
      const alreadyClosed = /already closed/i.test(data.error || '');
      if ((!res.ok || !data.success) && !alreadyClosed) {
        // Something genuinely blocked the close — offer the waiter's help.
        const phone = isSelfService
          ? ''
          : data.restaurant_phone || context?.restaurant_phone || '';
        const callWaiter = await confirm({
          title: t('guest.menu.leave.failedTitle'),
          description: `${data.error || t('guest.menu.askStaff')}${
            phone ? ` ${t('guest.menu.leave.canCallWaiter')}` : ''
          }`,
          confirmText: phone ? t('guest.menu.callWaiter') : t('guest.menu.ok'),
          cancelText: t('guest.menu.close'),
          tone: 'destructive',
        });
        if (callWaiter && phone) window.location.href = `tel:${phone}`;
        return;
      }
      // Success, or already closed by staff: either way fall through and clean up this device the same way.
      sessionStorage.removeItem('customerSession');
      sessionStorage.removeItem('customerToken');
      sessionStorage.removeItem('customerPlacedOrders');
      sessionStorage.removeItem('customerCart');
      sessionStorage.removeItem('customerBillPaid');
      sessionStorage.removeItem('customerBillRating');
      sessionStorage.removeItem('customerOrderRating');
      sessionStorage.removeItem('customerActiveOrder');
      if (session.table_id) {
        sessionStorage.removeItem(`paxConfirmed:${session.table_id}`);
      }
      setSession(null);
      setCart([]);
      setPlacedOrders([]);
      setPlacedOrder(null);
      setCelebration(null);
      setActiveOrderNumber('');
      await alertDialog({
        title: t('guest.menu.leave.freedTitle'),
        description: t('guest.menu.leave.freedBody'),
      });
      // Sign the guest out and return to login, keeping the table token so the next guest lands at this table.
      customerAuth.clear();
      const qrToken = params.get('token');
      navigate(
        qrToken
          ? `/customer/login?token=${encodeURIComponent(qrToken)}`
          : '/customer/account-login',
        { replace: true }
      );
    } catch (err) {
      console.error(err);
      await alertDialog({
        title: t('guest.menu.networkErrorTitle'),
        description: t('guest.menu.serverUnreachable'),
        tone: 'destructive',
      });
    }
  };

  // Poll the table's live status while seated, so the Shared badge appears or clears as parties come and go.
  useEffect(() => {
    if (!session?.session_id) return undefined;
    const qrToken = sessionStorage.getItem('customerToken') || token;
    if (!qrToken) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(
          `${API_URL}/users/customer/table-status/?token=${encodeURIComponent(qrToken)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          setSharedNow((data.data?.parties || 0) > 1);
        }
      } catch {
        /* transient network error — keep the last known value */
      }
    };
    check();
    /* Another party joining or leaving this table is a server-side event, so
       the push is what makes the Shared badge honest the moment it happens. */
    const unsub = subscribeRealtime(['table', 'session'], () => check(),
      { token: customerAuth.token(), app: 'customer' });
    const id = setInterval(check, socketLive ? 180000 : 45000);
    return () => {
      cancelled = true;
      unsub();
      clearInterval(id);
    };
  }, [session?.session_id, token, socketLive]);

  // Pick a sitting up on this device: the table session, the party-size answer and every order already sent.
  const adoptSitting = (s) => {
    adoptedSittingRef.current = s.session_id;
    sessionStorage.setItem('customerToken', s.token || token || '');
    sessionStorage.setItem('customerSession', JSON.stringify(s));
    sessionStorage.setItem(`paxConfirmed:${s.table_id}`, '1');
    setSession(s);
    setPaxModal(false);
    if (s.active_order_number) {
      setActiveOrderNumber(s.active_order_number);
      sessionStorage.setItem('customerActiveOrder', s.active_order_number);
    }
    const orders = s.orders || [];
    if (orders.length === 0) return;
    setOrderStatuses((prev) => ({
      ...prev,
      ...Object.fromEntries(orders.map((o) => [o.order_number, o.status])),
    }));
    setPlacedOrders((prev) => {
      // The server's copy refreshes what this tab already holds and restores what it lost; newest first.
      const known = new Set(prev.map((p) => p.order_number));
      const next = [
        ...prev.map((p) => {
          const srv = orders.find((o) => o.order_number === p.order_number);
          return srv ? { ...p, ...srv } : p;
        }),
        ...orders.filter((o) => !known.has(o.order_number)),
      ].sort((a, b) => new Date(b.placed_at || 0) - new Date(a.placed_at || 0));
      sessionStorage.setItem('customerPlacedOrders', JSON.stringify(next));
      return next;
    });
  };

  // Ask the server for the guest's own open sitting before deciding whether to prompt for the party size.
  useEffect(() => {
    const authToken = customerAuth.token();
    if (browseOnly || preorderId || !authToken) {
      setMySitting(null);
      return undefined;
    }
    let cancelled = false;
    // The page's own table token rides along: a hold staff booked by NAME
    // alone carries no phone for identity to find, but the table it is on is
    // the very one this QR names — the server adopts it instead of asking the
    // guest to pick their own table and party size all over again.
    fetch(`${API_URL}/users/customer/sitting/${token ? `?token=${encodeURIComponent(token)}` : ''}`, {
      headers: { Authorization: `Customer ${authToken}` },
    })
      .then((r) => r.json().then((d) => (r.ok && d.success ? d.data || null : null)))
      .catch(() => null)
      .then((s) => {
        if (!cancelled) setMySitting(s);
      });
    return () => {
      cancelled = true;
    };
  }, [browseOnly, preorderId, token]);

  // Reconcile the server's sitting with the table this page opened on: the same table (or no table yet) adopts it
  // silently; another table's QR is refused, since a guest sits at one table at a time.
  useEffect(() => {
    if (!mySitting || loading) return;
    const pageTable = context?.table_id ?? null;
    if (pageTable != null && String(pageTable) !== String(mySitting.table_id)) {
      setSeatedElsewhere(mySitting);
      return;
    }
    if (pageTable == null && !token && mySitting.token) {
      // Opened from a poster / menu QR or a bare link: reload on the sitting's own table so the menu carries it.
      navigate(`/customer/menu?token=${encodeURIComponent(mySitting.token)}`, { replace: true });
      return;
    }
    if (adoptedSittingRef.current === mySitting.session_id) return;
    adoptSitting(mySitting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySitting, loading, context?.table_id]);

  // Back to the guest's own table: adopt the sitting the server handed us and reload the menu on that table.
  const goToMySitting = () => {
    const s = seatedElsewhere;
    if (!s) return;
    setSeatedElsewhere(null);
    setTableBlocked('');
    setShowTablePicker(false);
    setMySitting(s);
    adoptSitting(s);
    navigate(`/customer/menu?token=${encodeURIComponent(s.token || '')}`, { replace: true });
  };

  // Prompt for party size once per table before ordering, when the table is known from the QR token.
  const knownTableId = context?.table_id || session?.table_id || null;
  useEffect(() => {
    // A browse-only guest isn't reserving the table, and a pre-ordering guest already has a reservation.
    if (browseOnly || preorderId) return;
    /* Kiosk/self-service: every order stands alone and is paid up front, so
       there is no sitting to size — "How many are dining?" is never asked. */
    if (isSelfService) return;
    if (!menu || !token || !knownTableId) return;
    // Wait for the server's word on the guest's open sitting: one at this table needs no party size again, and
    // one at another table is refused above rather than re-asked here.
    if (mySitting === undefined || mySitting) return;
    if (sessionStorage.getItem(`paxConfirmed:${knownTableId}`)) return;
    setPaxValue(session?.pax || 2);
    setPaxError('');
    setPaxModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, token, knownTableId, mySitting]);

  // Confirm the party size and reserve the table; an occupied table can still be JOINED when seats remain.
  const reserveWithPax = async (share) => {
    const sharing = share === true; // guard: onClick passes the event
    setPaxSaving(true);
    setPaxError('');
    try {
      const profile = customerAuth.profile() || {};
      const name =
        [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
        profile.name || '';
      const res = await fetch(`${API_URL}/users/customer/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token, name, phone: profile.phone || '', pax: paxValue,
          ...(sharing ? { share: true } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409 && data.seated_at) {
        // The guest's own sitting is still open at another table - one table at a time, so send them back to it.
        setSeatedElsewhere(data.seated_at);
        setBlockedPhone(isSelfService ? '' : data.restaurant_phone || '');
        setPaxModal(false);
        return;
      }
      if (res.status === 409) {
        if (!sharing && data.can_share) {
          // Another party holds the table but seats remain - offer to join as a separate party with a separate bill.
          setPaxSaving(false);
          const seatNote =
            data.seats_left != null
              ? ` ${t(data.seats_left === 1
                ? 'guest.menu.table.seatsLeftOne'
                : 'guest.menu.table.seatsLeftMany', { count: data.seats_left })}`
              : '';
          const ok = await confirm({
            title: t('guest.menu.table.occupiedPlain'),
            description: t('guest.menu.table.shareBody', { seats: seatNote }),
            confirmText: t('guest.menu.table.shareConfirm'),
            cancelText: t('guest.menu.notNow'),
          });
          if (ok) {
            await reserveWithPax(true);
            return;
          }
        }
        // Table full (or the guest declined) — block ordering.
        setTableBlocked(data.error || t('guest.menu.table.occupiedFallback'));
        setBlockedPhone(isSelfService ? '' : data.restaurant_phone || '');
        setPaxModal(false);
        return;
      }
      if (!res.ok || !data.success) {
        setPaxError(data.error || t('guest.menu.pax.failed'));
        return;
      }
      sessionStorage.setItem('customerToken', data.data.token || token);
      sessionStorage.setItem('customerSession', JSON.stringify(data.data));
      sessionStorage.setItem(`paxConfirmed:${data.data.table_id}`, '1');
      setSession(data.data);
      setPaxModal(false);
    } catch (err) {
      console.error(err);
      setPaxError(t('guest.menu.pax.networkError'));
    } finally {
      setPaxSaving(false);
    }
  };

  // Text-search filter over the menu's sections.
  const filteredSections = useMemo(() => {
    if (!menu?.sections) return [];
    const needle = search.trim().toLowerCase();
    return menu.sections
      .map((s) => ({
        ...s,
        items: (s.items || []).filter((it) => {
          if (!needle) return true;
          return (
            (it.name || '').toLowerCase().includes(needle) ||
            (it.description || '').toLowerCase().includes(needle) ||
            (it.variants || []).some((v) => (v.name || '').toLowerCase().includes(needle)) ||
            (s.name || '').toLowerCase().includes(needle)
          );
        }),
      }))
      .filter((s) => (s.items || []).length > 0);
  }, [menu, search]);

  // Category chips for the active section, imaged from the category master and hidden when none carry one.
  const dishCategories = useMemo(() => {
    const secs =
      activeSection === 'all'
        ? filteredSections
        : filteredSections.filter((s) => String(s.id) === String(activeSection));
    const cats = [];
    secs.forEach((s) =>
      (s.items || []).forEach((it) => {
        if (!it.dish_category) return;
        let cat = cats.find((c) => c.name === it.dish_category);
        if (!cat) {
          cat = { name: it.dish_category, image: null };
          cats.push(cat);
        }
        if (!cat.image) cat.image = it.dish_category_image || it.image || null;
      })
    );
    return cats;
  }, [filteredSections, activeSection]);

  const displaySections = useMemo(() => {
    let secs =
      activeSection === 'all'
        ? filteredSections
        : filteredSections.filter((s) => String(s.id) === String(activeSection));
    if (activeCat !== 'all' && dishCategories.some((c) => c.name === activeCat)) {
      secs = secs
        .map((s) => ({
          ...s,
          items: (s.items || []).filter((it) => it.dish_category === activeCat),
        }))
        .filter((s) => (s.items || []).length > 0);
    }
    return secs;
  }, [filteredSections, activeSection, activeCat, dishCategories]);

  const dietBadge = (item) => {
    if (item.is_vegan) return { label: t('guest.menu.diet.vegan'), cls: 'bg-green-100 text-green-700' };
    if (item.is_vegetarian) return { label: t('guest.menu.diet.veg'), cls: 'bg-green-100 text-green-700' };
    return { label: t('guest.menu.diet.nonVeg'), cls: 'bg-red-100 text-red-700' };
  };

  // A variant's label: size, then its name, else "Regular", with the quantity appended when present.
  const variantLabel = (v) => {
    const base = v.size || v.name || t('guest.menu.variant.regular');
    // A bare number without a unit (e.g. "· 1") reads as a count, not a size.
    const qty = v.quantity != null && v.unit ? `${v.quantity} ${v.unit}` : '';
    return qty ? `${base} · ${qty}` : base;
  };

  // A public page has no tenant settings, so the shared formatter's MUR default applies.
  const formatPrice = (p) => formatCurrencyWith(p, null);

  // ---------------------------------------------------------------- cart --
  const cartKey = (variantId, addons) =>
    `${variantId}:${addons.map((a) => `${a.addon_id}x${a.qty || 1}`).sort().join(',')}`;

  const addToCart = (item, v, addonQty = {}) => {
    /* Browsing a menu there is no counter to order from — nothing to add to.
       At a KIOSK the cart fills as normal and the sign-in is asked for once,
       when the order is sent (see placeOrder), so a guest chooses their food
       first and proves who they are once. */
    if (readOnlyMenu) {
      promptLogin();
      return;
    }
    // The bill is settled, so this sitting is closed; ordering again starts a
    // fresh session and order number. Self-service is exempt: there, every
    // order was paid as it was placed, and the NEXT cart simply becomes a new
    // order with its own number — ordering again is the normal case, not a
    // closed sitting being reopened.
    if (billPaid && !isSelfService) {
      confirm({
        title: t('guest.menu.billPaidTitle'),
        description: t('guest.menu.billPaidBody'),
        confirmText: t('guest.menu.signInAgain'),
        cancelText: t('guest.menu.notNow'),
      }).then((ok) => { if (ok) finishAfterPayment(); });
      return;
    }
    /* Each chosen add-on carries its QTY (2 × Dal Makhani), and the line's
       unit price folds price × qty in — the bill charges what the sheet said. */
    const addons = (v.addons || [])
      .map((a) => ({ ...a, qty: Number(addonQty[a.addon_id]) || 0 }))
      .filter((a) => a.qty > 0);
    const unitPrice =
      (v.is_free ? 0 : Number(v.price) || 0) +
      addons.reduce((s, a) => s + (Number(a.price) || 0) * a.qty, 0);
    // The same unit without its tax — what the order carries; the bill adds
    // the tax back, so the guest pays exactly the price on the sheet.
    const unitNet =
      (v.is_free ? 0 : Number(v.price_net ?? v.price) || 0) +
      addons.reduce((s, a) => s + (Number(a.price_net ?? a.price) || 0) * a.qty, 0);
    const key = cartKey(v.id, addons);
    setCart((prev) => {
      const existing = prev.find((l) => l.key === key);
      if (existing) {
        return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + 1 } : l));
      }
      return [
        ...prev,
        {
          key,
          dishId: item.dish_id,
          lineId: item.id,
          variantId: v.id,
          name: item.name,
          variant: variantLabel(v),
          // Carried for the order-placed screen: the dish's own photo on the
          // receipt line, and the kitchen's estimate behind "Ready in X min".
          image: item.image || null,
          totalTime: Number(item.total_time) || 0,
          unitPrice,
          unitNet,
          addons,
          qty: 1,
        },
      ];
    });
    setPlacedOrder(null);
  };

  /* Written on every change rather than at the moment of leaving: there is no
     reliable "about to navigate" hook, and the cart is small. */
  useEffect(() => {
    try {
      if (cart.length) sessionStorage.setItem('customerCart', JSON.stringify(cart));
      else sessionStorage.removeItem('customerCart');
    } catch { /* no storage — the cart simply stays in memory */ }
  }, [cart]);

  // ADD on a card: a single plain variant goes straight to the cart, anything else opens the sheet.
  const openItem = (item) => {
    const variants = item.variants || [];
    if (variants.length === 0) return;
    if (variants.length === 1 && (variants[0].addons || []).length === 0) {
      addToCart(item, variants[0]);
      return;
    }
    setPicker({ item, variantId: variants[0].id, addons: {} });
  };

  // Tapping the card opens the sheet - where the description, ingredients and allergens are read before adding.
  const openDetails = (item) => {
    const variants = item.variants || [];
    setPicker({ item, variantId: variants[0]?.id ?? null, addons: {} });
  };

  /* What the kitchen pairs this dish with - the Recommended pairings set on
     the dish (Add On Items) - resolved against THIS menu, so a pairing that is
     not on the menu, or is 86'd at this branch, simply does not show. Each
     one opens its own sheet rather than going straight to the cart: a lassi
     still has a size to choose. */
  const pairingsFor = (item) => {
    const order = new Map((item.recommended || []).map((r, i) => [r.dish_id, i]));
    if (!order.size) return [];
    const seen = new Set();
    const out = [];
    for (const it of (menu?.sections || []).flatMap((s) => s.items || [])) {
      if (!order.has(it.dish_id) || it.dish_id === item.dish_id || seen.has(it.dish_id)) continue;
      if (it.is_available === false || it.available_here === false) continue;
      if (!(it.variants || []).length) continue;
      seen.add(it.dish_id);
      out.push(it);
    }
    return out.sort((a, b) => order.get(a.dish_id) - order.get(b.dish_id)).slice(0, 6);
  };
  const pairingPrice = (p) => {
    const prices = (p.variants || []).map((x) => (x.is_free ? 0 : Number(x.price) || 0));
    if (!prices.length) return '';
    const low = formatPrice(Math.min(...prices));
    return prices.length > 1 ? t('guest.picker.from', { price: low }) : low;
  };

  const changeQty = (key, delta) =>
    setCart((prev) =>
      prev
        .map((l) => (l.key === key ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0)
    );

  // Drop one add-on from a cart line, recomputing the line's identity key and merging with a matching line.
  const removeAddon = (lineKey, addonId) =>
    setCart((prev) => {
      const line = prev.find((l) => l.key === lineKey);
      if (!line) return prev;
      const addons = line.addons.filter((a) => a.addon_id !== addonId);
      const removed = line.addons.find((a) => a.addon_id === addonId);
      const newKey = cartKey(line.variantId, addons);
      const updated = {
        ...line,
        key: newKey,
        addons,
        unitPrice: line.unitPrice - (Number(removed?.price) || 0) * (removed?.qty || 1),
        unitNet: (line.unitNet ?? line.unitPrice)
          - (Number(removed?.price_net ?? removed?.price) || 0) * (removed?.qty || 1),
      };
      const rest = prev.filter((l) => l.key !== lineKey);
      const twin = rest.find((l) => l.key === newKey);
      if (twin) {
        return rest.map((l) =>
          l.key === newKey ? { ...l, qty: l.qty + updated.qty } : l
        );
      }
      // Keep the line in its original position.
      return prev.map((l) => (l.key === lineKey ? updated : l));
    });

  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const cartTotal = cart.reduce((s, l) => s + l.qty * l.unitPrice, 0);

  /* The mock payment sheet (self-service): {amount, method, processing} or
     null. It opens INSTEAD of placing the order; only its Pay button re-enters
     placeOrder with paid === true. Demo payment — no money moves. */
  const [paySheet, setPaySheet] = useState(null);
  /* Two doors lead to the same payment now — the Pay button and a wallet QR
     read by the camera — so the latch is a ref, not the sheet's own state: a
     scan landing in the same instant as a tap must not send the order twice. */
  const payingRef = useRef(false);
  const confirmMockPayment = () => {
    if (payingRef.current) return;
    payingRef.current = true;
    // Which button the guest pressed rides along with the order: the till and
    // the bill say "Card · demo payment" rather than a bare "paid", and a
    // counter that has to answer "how did they pay?" has an answer.
    const method = paySheet?.method || '';
    setPaySheet((s) => (s ? { ...s, processing: true } : s));
    setTimeout(() => {
      payingRef.current = false;
      setPaySheet(null);
      placeOrder(true, method);
    }, 1200);
  };

  const placeOrder = async (paidArg, methodArg) => {
    // Button clicks pass the event object — only the sheet passes literal true.
    const paid = paidArg === true;
    /* The one sign-in in the counter flow. The cart is already in this tab's
       storage, so the guest comes back to the dishes they chose, not an empty
       basket, and the next tap on this button sends the order. */
    if (browseOnly) {
      setCartOpen(false);
      promptLogin();
      return;
    }
    // Pre-order mode: save the cart onto the reservation and return to it.
    if (preorderId) {
      setPlacing(true);
      setOrderError('');
      try {
        const res = await fetch(`${API_URL}/customers/reservations/${preorderId}/preorder/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Customer ${customerAuth.token()}`,
          },
          body: JSON.stringify({
            note: orderNote.trim(),
            total: cartTotal,
            items: cart.map((l) => ({
              dish_id: l.dishId,
              variant_id: l.variantId,
              menu_section_item_id: l.lineId,
              name: l.variant ? `${l.name} (${l.variant})` : l.name,
              quantity: l.qty,
              unit_price: l.unitNet ?? l.unitPrice,
              addons: l.addons.map((a) => ({
                addon_id: a.addon_id,
                name: a.name,
                price: a.price_net ?? a.price,
                qty: a.qty || 1,
              })),
            })),
          }),
        });
        // The server may answer with a non-JSON page, so don't let the parse failure look like a network error.
        let data = null;
        try { data = await res.json(); } catch { data = null; }
        if (!res.ok || !data?.success) {
          if (res.status === 401) {
            setOrderError(t('guest.menu.preorder.sessionExpired'));
          } else {
            setOrderError(
              data?.error ||
              t('guest.menu.preorder.saveFailed', { status: res.status }),
            );
          }
          return;
        }
        setCart([]);
        setOrderNote('');
        setCartOpen(false);
        await alertDialog({
          title: 'Pre-order saved',
          description:
            data.message ||
            'Your dishes are noted — the kitchen starts them the moment you check in.',
        });
        const rid = directRestaurant || context?.restaurant_id || '';
        navigate(`/customer/reserve?restaurant=${rid}`);
      } catch (err) {
        console.error(err);
        setOrderError('Network error while saving your pre-order.');
      } finally {
        setPlacing(false);
      }
      return;
    }
    /* No table is not a problem to solve any more: it IS the table-less order,
       paid up front and filed on the branch's ONLINE counter. The old detour
       to the table picker existed only because a branch could switch such
       orders off. */
    /* Self-service pays FIRST: the payment sheet opens over the cart, and
       only its Pay button comes back here with the payment "made". */
    if (isSelfService && !paid) {
      setPaySheet({ amount: cartTotal, method: 'card', processing: false });
      return;
    }
    setPlacing(true);
    setOrderError('');
    try {
      const profile = customerAuth.profile() || {};
      const res = await fetch(`${API_URL}/users/orders/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_id: session?.restaurant_id || context?.restaurant_id,
          // The guest's OWN device. Said explicitly because the branch may
          // also run a kiosk, and without this every phone order there was
          // counted as kiosk traffic. Ignored for a seated order, which is
          // dine-in whatever the phone says.
          origin: 'APP',
          // Null-safe: a table-less website order carries the BRANCH instead,
          // and the backend files it on the ONLINE counter.
          table_id: session?.table_id || null,
          location_id: context?.location_id || null,
          // Our party's table session, so the backend appends this round to the party's open order.
          session_id: session?.session_id || null,
          customer_name:
            session?.customer_name ||
            [profile.first_name, profile.last_name].filter(Boolean).join(' '),
          customer_phone: session?.customer_phone || profile.phone || '',
          // An empty list lets the backend infer the menus from each line, which also covers a cart spanning two.
          menu_ids: [],
          // Paid on the way in. The server only records it where the branch
          // takes money up front, so this can never mark a table order paid.
          ...(paid ? { paid: true, payment_method: methodArg || '' } : {}),
          notes: orderNote.trim(),
          items: cart.map((l) => ({
            dish_id: l.dishId,
            variant_id: l.variantId,
            menu_section_item_id: l.lineId,
            name: l.variant ? `${l.name} (${l.variant})` : l.name,
            quantity: l.qty,
            unit_price: l.unitNet ?? l.unitPrice,
            addons: l.addons.map((a) => ({
              addon_id: a.addon_id,
              name: a.name,
              price: a.price_net ?? a.price,
              qty: a.qty || 1,
            })),
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setOrderError(data.error || 'Could not place your order. Please try again.');
        return;
      }
      // The same order number means this round was added onto the sitting's open order.
      const appended =
        !!activeOrderNumber && data.data?.order_number === activeOrderNumber;
      setPlacedOrder({ ...data.data, appended });
      if (data.data?.order_number) {
        setActiveOrderNumber(data.data.order_number);
        sessionStorage.setItem('customerActiveOrder', data.data.order_number);
      }
      // Snapshot the order for the Orders tab + Pay Bill.
      const snapshot = {
        order_number: data.data?.order_number,
        placed_at: new Date().toISOString(),
        total: cartTotal,
        note: orderNote.trim(),
        items: cart.map((l) => ({
          name: l.name,
          variant: l.variant,
          addons: l.addons.map((a) => (a.qty > 1 ? `${a.qty}× ${a.name}` : a.name)),
          qty: l.qty,
          unitPrice: l.unitPrice,
        })),
      };
      setPlacedOrders((prev) => {
        // An appended round merges into its existing card - one order, one card - even before the first poll.
        const existing = prev.find((p) => p.order_number === snapshot.order_number);
        const next = existing
          ? prev.map((p) =>
              p.order_number === snapshot.order_number
                ? {
                    ...p,
                    total: (Number(p.total) || 0) + snapshot.total,
                    note: [p.note, snapshot.note].filter(Boolean).join('\n'),
                    items: [...(p.items || []), ...snapshot.items],
                  }
                : p
            )
          : [snapshot, ...prev];
        sessionStorage.setItem('customerPlacedOrders', JSON.stringify(next));
        return next;
      });
      /* Paid at the counter: the guest gets the full "it went through" screen
         rather than a banner they would have to go looking for — the order
         number, the wait, where it lands and what was charged, in one place.
         Built from the cart here because the next line empties it. */
      if (paid) {
        /* The kitchen cooks the dishes side by side, so the wait is the
           SLOWEST dish, not the sum of them; a menu carrying no times falls
           back to a plain ten minutes rather than inventing a promise. */
        const eta = Math.round(
          cart.reduce((m, l) => Math.max(m, Number(l.totalTime) || 0), 0) || 10
        );
        setCelebration({
          orderNumber: data.data?.order_number || '',
          items: cart.map((l) => ({
            name: l.name,
            variant: l.variant,
            image: l.image || null,
            qty: l.qty,
            unitPrice: l.unitPrice,
          })),
          total: cartTotal,
          etaMinutes: eta,
        });
      }
      setCart([]);
      setOrderNote('');
      setCartOpen(false);
      setTab('orders');
      /* Self-service: NOTHING is locked. This order was paid on the way in
         (payment_timing='before' server-side made it its own order with a
         fresh number), and the guest's next cart starts a new order the same
         way — ordering again is the point of a self-service counter, so the
         old one-order-per-visit latch is gone. */
    } catch (err) {
      console.error(err);
      setOrderError('Network error while placing the order.');
    } finally {
      setPlacing(false);
    }
  };

  const billTotal = placedOrders.reduce((s, o) => s + (Number(o.total) || 0), 0);
  // The booking advance riding on this sitting, reported per order and coming straight off what is paid.
  const billAdvance = placedOrders.reduce((s, o) => s + (Number(o.advance) || 0), 0);
  const billPayable = Math.max(0, billTotal - billAdvance);

  // Pay bill: a mock client-side flow whose paid flag and rating survive a refresh via sessionStorage.
  const [billPaid, setBillPaid] = useState(
    () => sessionStorage.getItem('customerBillPaid') === '1'
  );
  const [billRating, setBillRating] = useState(
    () => Number(sessionStorage.getItem('customerBillRating')) || 0
  );
  const [paySaving, setPaySaving] = useState(false);
  // Split module: closed | choosing a mode | one of the three modes.
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitMode, setSplitMode] = useState('pax'); // pax | custom | items
  const [splitCount, setSplitCount] = useState(2);   // custom people count
  const [itemPeople, setItemPeople] = useState(2);   // people in item split
  // line key -> the people who had that dish; empty means Shared, and a multi-select splits between just them.
  const [itemAssign, setItemAssign] = useState({});

  // Toggle one person on/off a dish; the Shared chip clears the selection.
  const toggleItemPerson = (key, who) => {
    setItemAssign((prev) => {
      if (who === 'shared') return { ...prev, [key]: [] };
      const cur = Array.isArray(prev[key]) ? prev[key] : [];
      const next = cur.includes(who) ? cur.filter((i) => i !== who) : [...cur, who];
      return { ...prev, [key]: next };
    });
  };

  // Every ordered line across all orders — feeds the by-item split.
  const billLines = placedOrders.flatMap((o, oi) =>
    (o.items || []).map((it, ii) => ({
      key: `${o.order_number || oi}-${ii}`,
      name: it.name,
      variant: it.variant,
      qty: it.qty,
      total: (Number(it.qty) || 1) * (Number(it.unitPrice) || 0),
    }))
  );

  const paxCount = Math.max(1, Number(session?.pax) || 1);

  const openSplit = () => {
    setSplitOpen(true);
    setSplitMode('pax');
    setSplitCount(Math.max(2, paxCount));
    setItemPeople(Math.max(2, paxCount));
    setItemAssign({});
  };

  // Per-person totals: each dish is split among the people ticked on it, and Shared items across the table.
  const itemSplitTotals = (() => {
    const totals = Array.from({ length: itemPeople }, () => 0);
    let shared = 0;
    billLines.forEach((ln) => {
      const eaters = (Array.isArray(itemAssign[ln.key]) ? itemAssign[ln.key] : [])
        .filter((i) => typeof i === 'number' && i < itemPeople);
      if (eaters.length === 0) shared += ln.total;
      else eaters.forEach((i) => { totals[i] += ln.total / eaters.length; });
    });
    const gross = totals.map((t) => t + shared / itemPeople);
    // The booking advance belongs to the whole table, so it comes off every share proportionally.
    const factor = billTotal > 0 ? billPayable / billTotal : 1;
    return gross.map((t) => t * factor);
  })();

  const payFullBill = () => {
    setPaySaving(true);
    // Mock gateway round-trip; on success the session is closed, so any further order starts fresh.
    setTimeout(async () => {
      try {
        if (session?.session_id) {
          const rid = session.restaurant_id || context?.restaurant_id;
          await fetch(
            `${API_URL}/users/restaurant/${rid}/sessions/${session.session_id}/close/`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
          );
        }
      } catch { /* mock payment still succeeds; staff can free the table */ }
      setBillPaid(true);
      sessionStorage.setItem('customerBillPaid', '1');
      setPaySaving(false);
      setSplitOpen(false);
    }, 900);
  };

  const rateBill = (n) => {
    setBillRating(n);
    sessionStorage.setItem('customerBillRating', String(n));
  };

  // After paying, sign the guest out: ordering again means a new session and new order numbers.
  const finishAfterPayment = () => {
    sessionStorage.removeItem('customerSession');
    sessionStorage.removeItem('customerToken');
    sessionStorage.removeItem('customerPlacedOrders');
    sessionStorage.removeItem('customerBillPaid');
    sessionStorage.removeItem('customerBillRating');
    sessionStorage.removeItem('customerActiveOrder');
    if (session?.table_id) {
      sessionStorage.removeItem(`paxConfirmed:${session.table_id}`);
    }
    customerAuth.clear();
    const qrToken = params.get('token');
    navigate(
      qrToken
        ? `/customer/login?token=${encodeURIComponent(qrToken)}`
        : '/customer/account-login',
      { replace: true }
    );
  };

  const tableCode = session?.table_code || context?.table_code || '';
  const isSharedTable = !!(context?.is_shared || session?.is_shared || sharedNow);
  const branchName =
    context?.location_name || menu?.location_name || menu?.restaurant_name || '';
  const profile = customerAuth.profile() || {};
  const customerName =
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    profile.name || session?.customer_name || '';

  /* Dishes to lead the page with - available items with photos, capped at four, and never dressed as social proof. */
  const othersPlate = useMemo(() => {
    const items = (menu?.sections || []).flatMap((s) => s.items || []);
    const available = items.filter(
      (it) => it.is_available !== false && (it.variants || []).length > 0
    );
    const withImg = available.filter((it) => it.image);
    const rest = available.filter((it) => !it.image);
    // De-dup by dish, prefer ones with photos, cap at 4.
    const seen = new Set();
    return [...withImg, ...rest]
      .filter((it) => {
        if (seen.has(it.dish_id)) return false;
        seen.add(it.dish_id);
        return true;
      })
      .slice(0, 4);
  }, [menu]);

  // "Make it a meal": the cheapest priced dish not yet in the cart, shown once an order has started.
  const mealUpsell = useMemo(() => {
    if (!cart.length) return null;
    const inCart = new Set(cart.map((l) => l.dishId));
    const seen = new Set();
    const candidates = (menu?.sections || [])
      .flatMap((s) => s.items || [])
      .filter(
        (it) =>
          it.is_available !== false &&
          it.available_here !== false &&
          !inCart.has(it.dish_id) &&
          (it.variants || []).length > 0
      )
      .filter((it) => {
        if (seen.has(it.dish_id)) return false;
        seen.add(it.dish_id);
        return true;
      })
      .map((it) => {
        const prices = (it.variants || []).map((v) =>
          v.is_free ? 0 : Number(v.price) || 0
        );
        return { it, price: Math.min(...prices) };
      })
      .filter((c) => c.price > 0);
    if (!candidates.length) return null;
    candidates.sort((a, b) => a.price - b.price);
    return candidates[0];
  }, [menu, cart]);

  // Open a section from a home tile.
  const openSection = (sectionId) => {
    setActiveSection(sectionId == null ? 'all' : String(sectionId));
    setActiveCat('all');
    setView('browse');
    setTab('menu');
    window.scrollTo?.(0, 0);
  };

  // Veg mark and dish row are plain render helpers, NOT components: components defined here remount on every poll.
  const renderVegMark = (veg, className = '') => (
    <span
      className={`inline-flex items-center justify-center h-4 w-4 rounded-[3px] border-2 bg-white shrink-0 ${className}`}
      style={{ borderColor: veg ? '#1D9E75' : '#D85A30' }}
      title={veg ? 'Veg' : 'Non-Veg'}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: veg ? '#1D9E75' : '#D85A30' }}
      />
    </span>
  );

  // A SOCIAL-style dish row: image left, name/price middle, ADD right.
  const renderDishRow = (item) => {
    const available = item.is_available !== false && item.available_here !== false;
    const veg = !!(item.is_vegan || item.is_vegetarian);
    const variants = item.variants || [];
    const prices = variants.map((v) => (v.is_free ? 0 : Number(v.price) || 0));
    const minPrice = prices.length ? Math.min(...prices) : null;
    const customisable =
      variants.length > 1 || (variants[0]?.addons || []).length > 0;
    /* Every cart line of THIS dish, across variants and add-ons, so the card
       shows the dish's total and stays a stepper once anything is in the cart.
       "+" repeats the most recent customization (its line grows by one); a
       DIFFERENT customization is one tap away via the dish itself. */
    const dishLines = cart.filter((l) =>
      item.dish_id != null ? l.dishId === item.dish_id : l.lineId === item.id
    );
    const qtyInCart = dishLines.reduce((s, l) => s + l.qty, 0);
    const stepKey = dishLines.length ? dishLines[dishLines.length - 1].key : null;
    return (
      <div className={`py-4 flex gap-4 ${available ? '' : 'opacity-50'}`}>
        {/* Image tile with the veg mark pinned top-left, like SOCIAL */}
        <div className="relative shrink-0">
          {item.image ? (
            <img
              src={item.image}
              alt={item.name}
              className="h-24 w-24 rounded-xl object-cover"
            />
          ) : (
            <div className="h-24 w-24 rounded-xl bg-orange-50 text-orange-400 flex items-center justify-center">
              <Utensils className="h-8 w-8" />
            </div>
          )}
          {renderVegMark(veg, 'absolute top-1.5 left-1.5 shadow-sm')}
        </div>

        {/* Name · price · description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div
                className="text-[17px] font-medium text-gray-900 leading-snug"
                onClick={() => openDetails(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openDetails(item)}
              >
                {item.name}
              </div>
              <div className="mt-2 text-[17px] text-gray-700">
                {minPrice == null ? (
                  <span className="text-sm text-gray-400">On request</span>
                ) : minPrice === 0 ? (
                  'Free'
                ) : (
                  <>
                    {variants.length > 1 && (
                      <span className="text-xs text-gray-400">from </span>
                    )}
                    {formatPrice(minPrice)}
                  </>
                )}
              </div>
            </div>

            {/* ADD / stepper column. Just browsing? Then no ADD at all — a menu
                being READ is not a menu being ordered from, so the card offers
                the dish (tap for details) and nothing else. Unavailability
                still shows: that is menu information, not an order control. */}
            <div className="shrink-0 flex flex-col items-center">
              {!available ? (
                <span className="text-[10px] uppercase tracking-wider text-red-500 mt-2">
                  Unavailable
                </span>
              ) : readOnlyMenu ? null : qtyInCart > 0 ? (
                <span className="flex items-center rounded-xl border border-gray-200 bg-white shadow-sm">
                  <button
                    onClick={() => changeQty(stepKey, -1)}
                    className="h-10 w-9 flex items-center justify-center text-lg"
                    style={{ color: ORANGE }}
                    aria-label="Decrease"
                  >
                    −
                  </button>
                  <span className="text-sm font-semibold w-5 text-center text-gray-900">
                    {qtyInCart}
                  </span>
                  <button
                    onClick={() => changeQty(stepKey, 1)}
                    className="h-10 w-9 flex items-center justify-center text-lg"
                    style={{ color: ORANGE }}
                    aria-label="Increase"
                  >
                    +
                  </button>
                </span>
              ) : variants.length > 0 ? (
                <button
                  onClick={() => openItem(item)}
                  className="px-7 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm text-[15px] font-semibold tracking-wide"
                  style={{ color: ORANGE }}
                >
                  ADD
                </button>
              ) : null}
              {customisable && available && !readOnlyMenu && (
                <span className="text-xs text-gray-500 mt-1.5">customizable</span>
              )}
            </div>
          </div>

          {/* Description — one line, tap to expand/collapse */}
          {item.description && (
            <p
              onClick={() => toggleDesc(item.id)}
              className={`mt-2 text-[15px] text-gray-400 cursor-pointer ${
                expandedDesc.has(item.id) ? '' : 'line-clamp-1'
              }`}
            >
              {item.description}
              {expandedDesc.has(item.id) && (
                <span className="ml-1 text-xs font-medium" style={{ color: ORANGE }}>
                  less
                </span>
              )}
            </p>
          )}
          {item.spicy_level && (
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
              <span aria-hidden="true">
                {'🌶'.repeat(SPICY_PEPPERS[item.spicy_level] || 1)}
              </span>
              {t(SPICY_LABELS[item.spicy_level] || 'guest.menu.spicy.normal')}
            </div>
          )}
          {(item.complimentary || []).length > 0 && (
            <div className="mt-1 text-xs text-emerald-700">
              Comes with {item.complimentary.map((c) => c.name).join(', ')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact photo card for the "More to love" grid - a render helper, for the same remount reason.
  const renderDishCard = (item) => {
    const available = item.is_available !== false && item.available_here !== false;
    const veg = !!(item.is_vegan || item.is_vegetarian);
    const variants = item.variants || [];
    const prices = variants.map((v) => (v.is_free ? 0 : Number(v.price) || 0));
    const minPrice = prices.length ? Math.min(...prices) : null;
    // Same stepper rule as the dish row: total across the dish's cart lines, step the newest line.
    const dishLines = cart.filter((l) =>
      item.dish_id != null ? l.dishId === item.dish_id : l.lineId === item.id
    );
    const qtyInCart = dishLines.reduce((s, l) => s + l.qty, 0);
    const stepKey = dishLines.length ? dishLines[dishLines.length - 1].key : null;
    return (
      <div className={`rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden flex flex-col ${available ? '' : 'opacity-50'}`}>
        <div className="relative" onClick={() => openDetails(item)} role="button" tabIndex={0}>
          {item.image ? (
            <img src={item.image} alt={item.name} className="h-28 w-full object-cover" />
          ) : (
            <div className={`h-28 w-full flex items-center justify-center ${veg ? 'bg-green-50 text-green-300' : 'bg-red-50 text-red-300'}`}>
              <Utensils className="h-8 w-8" />
            </div>
          )}
          {renderVegMark(veg, 'absolute top-2 left-2 shadow-sm')}
          {item.spicy_level && (
            <span className="absolute top-2 right-2 rounded-full bg-red-500 text-white text-[10px] font-semibold px-2 py-0.5">
              {t(SPICY_LABELS[item.spicy_level] || 'guest.menu.spicy.normal')}
            </span>
          )}
        </div>
        <div className="p-3 flex-1 flex flex-col">
          <div
            className="text-[15px] font-medium text-gray-900 leading-snug"
            onClick={() => openDetails(item)}
            role="button"
            tabIndex={0}
          >
            {item.name}
          </div>
          <div className="mt-auto pt-2 flex items-center justify-between gap-2">
            <span className="font-semibold text-gray-900">
              {minPrice == null ? (
                <span className="text-xs text-gray-400 font-normal">On request</span>
              ) : minPrice === 0 ? 'Free' : formatPrice(minPrice)}
            </span>
            {available && minPrice != null && !readOnlyMenu && (
              qtyInCart > 0 ? (
                <span className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1" style={{ borderColor: ORANGE, color: ORANGE }}>
                  <button onClick={() => changeQty(stepKey, -1)} aria-label="Decrease">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="text-sm font-semibold w-4 text-center text-gray-900">{qtyInCart}</span>
                  <button onClick={() => changeQty(stepKey, 1)} aria-label="Increase">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => openItem(item)}
                  className="rounded-full border px-4 py-1.5 text-sm font-semibold"
                  style={{ borderColor: ORANGE, color: ORANGE }}
                >
                  Add
                </button>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    // The global stylesheet hides body overflow, so this standalone page must be its own scroll container.
    <div className="min-h-screen w-full bg-gray-100 text-gray-900 flex justify-center">
      <div className={`relative w-full max-w-[440px] bg-white h-screen overflow-y-auto shadow-xl ${FRAME_WIDE}`}>

      {/* ── Kiosk / wide screens: the sections as a standing rail ───────────
          On a phone the sections are a strip you swipe past; on a panel a
          metre tall that strip is at chest height and everything below it is
          a scroll away. Here they are a column that never moves, so a guest
          walks up and reads the whole menu's shape at once. Fixed rather than
          a flex child on purpose: the frame is one scrolling column, and the
          rail must not scroll with the dishes. */}
      {menu && (
        <aside className={`${RAIL_SHOW} fixed left-0 top-0 bottom-0 w-[268px] z-30 flex-col bg-white border-r border-gray-200`}>
          <div className="px-5 pt-6 pb-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">
              {t('guest.menu.rail.title')}
            </div>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 flex flex-col gap-1.5">
            <button
              onClick={() => {
                setView('home');
                setTab('menu');
                setActiveSection('all');
                setActiveCat('all');
                window.scrollTo?.(0, 0);
              }}
              className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left ${
                view === 'home' ? 'text-white' : 'text-gray-700'
              }`}
              style={view === 'home' ? { background: MAROON } : undefined}
            >
              <span
                className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: view === 'home' ? 'rgba(255,255,255,0.16)' : '#F6F1EE' }}
              >
                <Star className="h-5 w-5" style={{ color: view === 'home' ? '#fff' : ORANGE }} />
              </span>
              <span className="min-w-0 text-[15px] font-semibold truncate">
                {t('guest.menu.rail.featured')}
              </span>
            </button>
            {(menu.sections || []).map((sec) => {
              // The section's own cover photo, falling back to the first dish
              // image it contains — the same rule the home tiles use.
              const img = sec.image || (sec.items || []).find((it) => it.image)?.image;
              const on = view === 'browse' && String(activeSection) === String(sec.id);
              return (
                <button
                  key={`rail-${sec.id}`}
                  onClick={() => openSection(sec.id)}
                  className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left ${
                    on ? 'text-white' : 'text-gray-700'
                  }`}
                  style={on ? { background: MAROON } : undefined}
                >
                  <span className="h-11 w-11 rounded-xl overflow-hidden shrink-0 bg-gray-100 flex items-center justify-center">
                    {img ? (
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-[11px] font-bold uppercase text-gray-400">
                        {sec.name.slice(0, 2)}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold truncate">{sec.name}</span>
                    <span className={`block text-xs ${on ? 'text-white/70' : 'text-gray-400'}`}>
                      {t('guest.menu.rail.dishes', { count: (sec.items || []).length })}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>
      )}

      {/* ── Sticky header: dark shell — identity · table chip · search ────── */}
      <header className="sticky top-0 z-20 shadow-sm">
        <div className="rounded-b-3xl pb-4" style={{ background: MAROON }}>
          <div className="max-w-2xl mx-auto px-4 pt-3 flex items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: ORANGE }}
            >
              <Utensils className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-semibold text-white truncate leading-snug">
                {/* Never the menu's name — this names the place. */}
                {branchName || 'Restaurant'}
              </div>
              <div className="mt-0.5 text-xs leading-tight flex items-center gap-2" style={{ color: '#F3B48C' }}>
                {customerName && (
                  <span className="inline-flex items-center gap-1 min-w-0 truncate">
                    <User className="h-3 w-3" />
                    {customerName}
                  </span>
                )}
                {activeOrderNumber && (
                  <span className="shrink-0 font-mono font-semibold">
                    #{activeOrderNumber}
                  </span>
                )}
              </div>
            </div>
            {/* English or French, right in the header: the guest has no other
                chrome to reach it from, and a diner who reads French should
                not have to read English to find the switch. */}
            <GuestLanguageSwitch />
            {/* No waiter to call at a self-service counter */}
            {context?.restaurant_phone && !isSelfService && (
              <a
                href={`tel:${context.restaurant_phone}`}
                aria-label={t('guest.menu.callWaiter')}
                title={t('guest.menu.callWaiter')}
                className="shrink-0 h-10 w-10 rounded-full border border-white/30 text-white flex items-center justify-center"
              >
                <Phone className="h-4 w-4" />
              </a>
            )}
          </div>

          {/* Table chip row: table · shared · hours */}
          <div className="max-w-2xl mx-auto px-4 mt-3">
            <div className="rounded-xl bg-white/10 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white">
              {tableCode ? (
                <button
                  onClick={session?.session_id ? leaveTable : undefined}
                  title={session?.session_id ? 'Leave this table' : undefined}
                  className="inline-flex items-center gap-1.5 font-semibold"
                >
                  <MapPin className="h-4 w-4" style={{ color: ORANGE }} />
                  Table {tableCode}
                  {session?.session_id && <LogOut className="h-3.5 w-3.5 opacity-70" />}
                </button>
              ) : browseOnly || canOrderWithoutTable ? null : (
                <button
                  onClick={() => setShowTablePicker(true)}
                  className="inline-flex items-center gap-1.5 font-semibold"
                  style={{ color: '#F3B48C' }}
                >
                  <Armchair className="h-4 w-4" />
                  Select table
                </button>
              )}
              {isSharedTable && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold">
                  <Users className="h-3 w-3" />
                  Shared
                </span>
              )}
              {/* The branch's whole window for TODAY, from its own hours —
                  "Open 9:00 AM – 10:00 PM"; a day the branch marked closed
                  says so rather than borrowing a closing time from nowhere. */}
              {context?.closed_today ? (
                <span className="inline-flex items-center gap-1 text-white/70 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  Closed today
                </span>
              ) : context?.opening_time && context?.closing_time ? (
                <span className="inline-flex items-center gap-1 text-white/70 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  Open {fmtTime(context.opening_time)} – {fmtTime(context.closing_time)}
                </span>
              ) : context?.closing_time ? (
                <span className="inline-flex items-center gap-1 text-white/70 text-xs">
                  <Clock className="h-3.5 w-3.5" />
                  Closes {fmtTime(context.closing_time)}
                </span>
              ) : null}
              {context?.last_order_time && (
                <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#F3B48C' }}>
                  <Ban className="h-3.5 w-3.5" />
                  Last order {fmtTime(context.last_order_time)}
                </span>
              )}
            </div>
          </div>

          {/* Search — always at hand; typing drops straight into browse */}
          {menu && tab === 'menu' && (
            <div className="max-w-2xl mx-auto px-4 mt-3">
              <div className="rounded-2xl bg-white shadow-lg flex items-center gap-2.5 px-4">
                <Search className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearch(v);
                    if (v && view !== 'browse') setView('browse');
                  }}
                  placeholder={`Craving something? Try "${(othersPlate[0]?.name || 'something tasty').toLowerCase()}"`}
                  className="flex-1 py-3 text-sm outline-none bg-transparent min-w-0"
                />
              </div>
            </div>
          )}
        </div>

        {/* Browse sub-header: back · search */}
        {tab === 'menu' && view === 'browse' && !loading && !error && menu && (
          <div className="border-t border-gray-100 bg-white">
            <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center gap-3">
              <button
                onClick={() => { setView('home'); setSearch(''); }}
                aria-label="Back"
                className="text-gray-800 p-1 -ml-1"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex-1" />
            </div>

            {/* Category pills — the menu's sections */}
            <div className="max-w-2xl mx-auto px-4 pb-1 flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {(menu.sections || []).map((s) => {
                const active = String(activeSection) === String(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => { setActiveSection(String(s.id)); setActiveCat('all'); }}
                    className={`shrink-0 px-5 py-2 rounded-full text-[16px] transition ${
                      active ? 'text-white font-medium' : 'text-gray-700 bg-gray-100'
                    }`}
                    style={active ? { background: MAROON } : {}}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>

            {/* The dish-category chip rail, imaged from the category master and hidden when no dish carries a category. */}
            {dishCategories.length > 0 && (
              <div className="max-w-2xl mx-auto px-4 py-3 flex gap-5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[{ name: 'all', image: null }, ...dishCategories].map((cat) => {
                  const active = activeCat === cat.name;
                  return (
                    <button
                      key={cat.name}
                      onClick={() => setActiveCat(cat.name)}
                      className="shrink-0 flex flex-col items-center w-24"
                    >
                      <span
                        className={`h-20 w-20 rounded-xl overflow-hidden shadow-sm border ${
                          active ? 'border-orange-400' : 'border-gray-100'
                        }`}
                      >
                        {cat.image ? (
                          <img src={cat.image} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="h-full w-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-700">
                            <Utensils className="h-7 w-7 text-white/80" />
                          </span>
                        )}
                      </span>
                      <span
                        className={`mt-2 text-sm text-center leading-tight ${
                          active ? 'font-medium' : 'text-gray-700'
                        }`}
                        style={active ? { color: ORANGE } : {}}
                      >
                        {cat.name === 'all' ? 'All' : cat.name}
                      </span>
                      <span
                        className={`mt-1.5 h-0.5 rounded-full transition-all ${
                          active ? 'w-16' : 'w-0'
                        }`}
                        style={{ background: ORANGE }}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      {/* Bottom padding clears the fixed bar: nav alone when the cart is empty, nav plus CONFIRM once it isn't. */}
      <main className={`max-w-2xl mx-auto px-4 ${cartCount > 0 ? 'pb-44' : 'pb-20'}`}>
        {/* Pre-order banner — cart goes onto the reservation, not the kitchen */}
        {preorderId && !loading && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3">
            <Clock className="h-5 w-5 shrink-0 text-emerald-700" />
            <span className="flex-1 text-sm text-emerald-900">
              Pre-ordering for your reservation — the kitchen starts these dishes
              the moment you check in.
            </span>
          </div>
        )}
        {/* Browse-only banner. Deliberately NOT an invitation to order: a
            guest reading the menu from the website is window-shopping, and
            every ordering affordance is off in this mode — no ADD, no table
            prompt, no order tabs — so the banner only says what this is. */}
        {browseOnly && !loading && (
          kioskBrowse ? (
            <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3 flex items-center gap-3">
              <ConciergeBell className="h-5 w-5 shrink-0" style={{ color: ORANGE }} />
              <span className="flex-1 text-sm text-gray-700">
                {t('guest.menu.browse.kioskInvite')}
              </span>
              <button
                type="button"
                onClick={promptLogin}
                className="shrink-0 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition"
                style={{ background: ORANGE }}
              >
                {t('guest.menu.browse.signInCta')}
              </button>
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3 flex items-center gap-3">
              <Eye className="h-5 w-5 shrink-0" style={{ color: ORANGE }} />
              <span className="flex-1 text-sm text-gray-700">
                You're browsing the menu — dishes and prices to read, nothing to
                order from here.
              </span>
            </div>
          )
        )}
        {/* The buffet counter above the menu, since a guest on the buffet is not here to browse dish prices. */}
        {tab === 'menu' && view === 'home' && (
          <BuffetCounterPanel
            token={token}
            spread={buffetSpread}
            pending={buffetSpread?.my_requests}
            asking={askingItem}
            onAsk={askForDish}
          />
        )}

        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto"
                 style={{ borderColor: ORANGE }} />
            <p className="mt-3 text-sm text-gray-500">Loading menu…</p>
          </div>
        )}

        {!loading && error && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {!loading && !error && !menu && menuChoices.length === 0 && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-8 text-center">
            <Utensils className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-600">
              No active menu has been published yet. Please ask the staff.
            </p>
          </div>
        )}

        {/* Menu chooser — several menus are live; pick one before browsing. */}
        {!loading && !error && !menu && menuChoices.length > 1 && (
          <div className="mt-6">
            <h2 className="text-[22px] font-semibold text-gray-800">Choose a menu</h2>
            <p className="text-sm text-gray-500 mt-1 mb-5">
              {menuChoices.length} menus are available right now — tap one to start.
            </p>
            <div className="grid grid-cols-1 gap-3">
              {menuChoices.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMenu(m.id)}
                  className="w-full rounded-2xl border border-gray-200 hover:border-orange-400 hover:shadow-md transition p-5 text-left flex items-center gap-4"
                >
                  <span
                    className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: '#FDE7D6', color: ORANGE }}
                  >
                    <BookOpen className="h-6 w-6" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-gray-900">{m.name}</span>
                    {m.description && (
                      <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">{m.description}</span>
                    )}
                  </span>
                  <ArrowLeft className="h-5 w-5 text-gray-300 rotate-180 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Order placed confirmation (shown on the menu tab too) */}
        {placedOrder && tab === 'menu' && (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="text-sm text-green-800">
              <div className="font-semibold">
                {placedOrder.appended
                  ? `Added to order ${placedOrder.order_number}!`
                  : `Order ${placedOrder.order_number} placed!`}
              </div>
              <div className="mt-0.5">
                {placedOrder.appended ? 'The new items have' : 'Your order has'} been
                sent to the kitchen{tableCode ? ` for table ${tableCode}` : ''}.
              </div>
            </div>
          </div>
        )}

        {/* Table prompt for menu-QR visitors who haven't picked a table — not
            while just browsing: picking a table IS the start of ordering. A
            kiosk branch never asks: ordering there needs no table at all. */}
        {!loading && !error && menu && needsTable && !placedOrder && tab === 'menu' && !browseOnly
          && !(isSelfService && context?.kiosk_mode !== false) && (
          <button
            onClick={() => setShowTablePicker(true)}
            className="w-full mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3 flex items-center gap-3 text-left hover:bg-orange-100/70 transition"
          >
            <Armchair className="h-5 w-5 shrink-0" style={{ color: ORANGE }} />
            <span className="text-sm text-orange-900">
              <span className="font-medium">Dining in?</span> Select your table to
              order straight from your phone.
            </span>
          </button>
        )}

        {/* ── MENU tab · HOME view: category tiles ─────────────────────────── */}
        {tab === 'menu' && view === 'home' && !loading && !error && menu && (
          <>
            {/* Category chips under the header: Popular stays active, and a section chip jumps into browse. */}
            <div className="mt-4 flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-sm"
                style={{ background: MAROON }}
              >
                Featured
              </button>
              {(menu.sections || []).map((s) => (
                <button
                  key={`home-chip-${s.id}`}
                  onClick={() => openSection(s.id)}
                  className="shrink-0 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-gray-700"
                >
                  {s.name}
                </button>
              ))}
            </div>

            {/* Hero: the house pick, from the same suggestion list the old social strip used. */}
            {othersPlate.length > 0 && (() => {
              const hero = othersPlate[0];
              const heroVariants = hero.variants || [];
              const heroPrices = heroVariants.map((x) => (x.is_free ? 0 : Number(x.price) || 0));
              const heroMin = heroPrices.length ? Math.min(...heroPrices) : null;
              // Cheapest-first, for the "Rs 225 tortilla · Rs 245 naan" price line.
              const heroSorted = [...heroVariants].sort(
                (a, b) =>
                  (a.is_free ? 0 : Number(a.price) || 0) -
                  (b.is_free ? 0 : Number(b.price) || 0)
              );
              const heroKcal = heroSorted[0]?.calories;
              return (
                <div className="mt-4 rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
                  <div
                    className="relative px-5 pt-5 pb-6"
                    style={{ background: `linear-gradient(135deg, ${ORANGE} 0%, #C24E08 100%)` }}
                  >
                    <h2 className="mt-3 text-white text-2xl font-semibold leading-tight pr-32">
                      {hero.name}
                    </h2>
                    {hero.image && (
                      <img
                        src={hero.image}
                        alt=""
                        onClick={() => openDetails(hero)}
                        className="absolute right-3 -bottom-6 h-28 w-28 rounded-full object-cover border-4 border-white/80 shadow-lg"
                      />
                    )}
                  </div>
                  <div className="px-5 py-4 flex items-end justify-between gap-3">
                    <div className="min-w-0">
                      {(hero.description || heroKcal) && (
                        <div className="text-sm text-gray-500 truncate pr-24">
                          {[hero.description, heroKcal ? `${heroKcal} kcal` : '']
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                      <div className="mt-1 text-xl font-bold text-gray-900">
                        {heroMin == null ? 'On request' : heroMin === 0 ? 'Free' : formatPrice(heroMin)}
                        {heroVariants.length > 1 && (
                          <span className="ml-1.5 text-xs font-normal text-gray-400">
                            {variantLabel(heroSorted[0])}
                            {heroSorted[1] && (
                              <>
                                {' · '}
                                {heroSorted[1].is_free
                                  ? 'Free'
                                  : formatPrice(Number(heroSorted[1].price) || 0)}{' '}
                                {variantLabel(heroSorted[1])}
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {heroMin != null && !readOnlyMenu && (
                      <button
                        onClick={() => openItem(hero)}
                        className="shrink-0 rounded-full px-6 py-2.5 text-white font-semibold shadow"
                        style={{ background: MAROON }}
                      >
                        Add +
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {menu.description && (
              <p className="mt-4 text-sm text-gray-500">{menu.description}</p>
            )}
            <h2 className="mt-6 mb-4 text-[22px] font-semibold text-gray-800">
              {readOnlyMenu ? 'What’s on the menu' : 'What would you like to order?'}
            </h2>

            {/* Menu switcher tiles first when several menus are live */}
            {menuChoices.length > 1 && (
              <div className="mb-4 flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {menuChoices.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => pickMenu(m.id)}
                    className={`shrink-0 px-5 py-2 rounded-full border text-[15px] transition ${
                      menu?.id === m.id ? 'text-white font-medium' : 'text-gray-700 border-gray-200'
                    }`}
                    style={menu?.id === m.id ? { background: ORANGE, borderColor: ORANGE } : {}}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {(menu.sections || []).map((s) => {
                // Prefer the section's own cover photo, falling back to the first dish image it contains.
                const img = s.image || (s.items || []).find((it) => it.image)?.image;
                return (
                  <button
                    key={s.id}
                    onClick={() => openSection(s.id)}
                    className="relative rounded-2xl overflow-hidden aspect-[4/5] text-left shadow-sm group"
                  >
                    {img ? (
                      <>
                        <img
                          src={img}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover group-active:scale-105 transition-transform"
                        />
                        <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      </>
                    ) : (
                      <span className="absolute inset-0 bg-gradient-to-b from-orange-400 to-orange-800" />
                    )}
                    {!img && (
                      <span className="absolute inset-x-0 top-1/3 flex justify-center">
                        <span className="text-white/90 font-bold uppercase tracking-widest text-sm border-2 border-white/70 rounded px-2 py-1">
                          {s.name.slice(0, 14)}
                        </span>
                      </span>
                    )}
                    <span className="absolute bottom-3 left-3 right-3 text-white text-xl font-semibold leading-snug drop-shadow">
                      {s.name}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* "Make it a meal" - a nudge to round the order off with the cheapest
                dish not already in the cart. Never while just browsing: an upsell
                is an order control, and there is no order. */}
            {mealUpsell && !readOnlyMenu && (
              <button
                onClick={() => openItem(mealUpsell.it)}
                className="mt-8 w-full rounded-2xl border border-amber-100 bg-amber-50 p-4 flex items-center gap-3 text-left active:scale-[0.99] transition"
              >
                <span className="h-11 w-11 rounded-xl bg-amber-400 text-white flex items-center justify-center text-xl shrink-0">
                  🍟
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-gray-900">Make it a meal</span>
                  <span className="block text-sm text-amber-700 truncate">
                    Add {mealUpsell.it.name} for just {formatPrice(mealUpsell.price)} more
                  </span>
                </span>
                <ChevronDown className="h-5 w-5 -rotate-90 text-amber-500 shrink-0" />
              </button>
            )}

            {/* "More to love": compact photo cards closing the page after the sections and upsell. */}
            {othersPlate.length > 1 && (
              <div className="mt-6">
                <h2 className="text-[20px] font-semibold text-gray-800 mb-3">More to love</h2>
                <div className="grid grid-cols-2 gap-3">
                  {othersPlate.slice(1).map((it) => (
                    <React.Fragment key={`love-${it.id}`}>{renderDishCard(it)}</React.Fragment>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <span className="text-xl">🔥</span>
              <p className="text-sm text-gray-600">
                An average adult needs 2000 kcal a day; needs vary.
              </p>
            </div>
          </>
        )}

        {/* ── MENU tab · BROWSE view: chips + dish rows ────────────────────── */}
        {tab === 'menu' && view === 'browse' && !loading && !error && menu && (
          <>
            {displaySections.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-14">
                No dishes match your search.
              </div>
            ) : (
              displaySections.map((section) => (
                <section key={section.id} className="pt-6">
                  <div className="text-center mb-2">
                    <h2 className="inline-block text-[22px] font-semibold text-gray-800">
                      {section.name}
                    </h2>
                    <div className="h-0.5 w-12 rounded-full mx-auto mt-1"
                         style={{ background: ORANGE }} />
                    {(section.available_from || section.available_until) && (
                      <div className="text-xs text-gray-400 mt-1">
                        {fmtTime(section.available_from) || ''}
                        {section.available_from || section.available_until ? ' – ' : ''}
                        {fmtTime(section.available_until) || ''}
                      </div>
                    )}
                  </div>
                  {section.description && (
                    <p className="text-xs text-gray-400 text-center mb-2">{section.description}</p>
                  )}
                  {/* Dishes ordered by category; on "All" each gets a heading, while a filtered chip already names it. */}
                  {(() => {
                    const groups = [];
                    (section.items || []).forEach((item) => {
                      const key = item.dish_category || '';
                      let g = groups.find((x) => x.key === key);
                      if (!g) { g = { key, items: [] }; groups.push(g); }
                      g.items.push(item);
                    });
                    // Named categories first (menu order), uncategorised last.
                    groups.sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : 0));
                    const showHeadings =
                      activeCat === 'all' &&
                      groups.filter((g) => g.key).length > 1;
                    return groups.map((g) => (
                      <div key={g.key || '_none'}>
                        {showHeadings && g.key && (
                          <div className="flex items-center gap-3 pt-4 pb-1">
                            <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                              {g.key}
                            </span>
                            <span className="flex-1 h-px bg-gray-100" />
                          </div>
                        )}
                        <div className="divide-y divide-dashed divide-gray-200">
                          {g.items.map((item) => (
                            <React.Fragment key={item.id}>{renderDishRow(item)}</React.Fragment>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </section>
              ))
            )}
          </>
        )}

        {/* ── ORDERS tab ───────────────────────────────────────────────────── */}
        {tab === 'orders' && (
          <div className="pt-6">
            <h2 className="text-[22px] font-semibold text-gray-800 mb-4">Your orders</h2>
            {/* Self-service: every order was paid as it was placed, so confirm
                the payment and say plainly that the next cart is a NEW order. */}
            {isSelfService && placedOrders.length > 0 && (
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 flex items-start gap-2.5">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div className="text-sm text-green-800">
                  <div className="font-semibold">
                    Payment received · {formatPrice(billTotal)}
                  </div>
                  <div className="mt-0.5">
                    Your order status updates below. Want more? Order again from the
                    menu — it becomes a new order with its own number, paid the same way.
                  </div>
                </div>
              </div>
            )}
            {placedOrders.length === 0 ? (
              <div className="text-center py-14 text-gray-400">
                <ConciergeBell className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No orders yet this sitting.</p>
                <p className="text-xs mt-1">Add dishes from the menu and confirm your order.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {placedOrders.map((o, i) => (
                  <div key={`${o.order_number}-${i}`} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">
                        Order {o.order_number || '—'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {o.placed_at
                          ? new Date(o.placed_at).toLocaleTimeString([], {
                              hour: 'numeric', minute: '2-digit',
                            })
                          : ''}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {(o.items || []).map((l, j) => (
                        <div key={j} className="flex items-start justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <span className="text-gray-800">{l.qty} × {l.name}</span>
                            {l.variant && (
                              <span className="text-gray-400"> · {l.variant}</span>
                            )}
                            {(l.addons || []).length > 0 && (
                              <div className="text-xs text-gray-400">+ {l.addons.join(', ')}</div>
                            )}
                            {/* This DISH's own progress — one order's dishes
                                arrive one by one, so each line says where it
                                is: cooking, ready, or served (with the time).
                                A cancelled order's lines stay quiet: their
                                last kitchen state would only mislead. */}
                            {(() => {
                              if (orderStatuses[o.order_number] === 'CANCELLED') return null;
                              const st = itemStatusLine(l, t, collectAtCounter);
                              return st ? (
                                <div className={`text-[11px] ${st.cls}`}>{st.text}</div>
                              ) : null;
                            })()}
                          </div>
                          <span className="text-gray-600 shrink-0">
                            {formatPrice(l.qty * l.unitPrice)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {o.note && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        {t('guest.menu.noteLabel')}: {o.note}
                      </div>
                    )}
                    <div className="mt-3 pt-2 border-t border-dashed border-gray-200 flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('guest.menu.orderTotal')}</span>
                      <span className="font-semibold text-gray-900">{formatPrice(o.total)}</span>
                    </div>
                    {(() => {
                      const st = orderStatuses[o.order_number] || 'ORDERED';
                      const meta = ORDER_STATUS_META[st] || ORDER_STATUS_META.ORDERED;
                      const Icon =
                        st === 'CANCELLED' ? Ban
                        : st === 'PREPARING' || st === 'READY' ? Clock
                        : CheckCircle2;
                      // Partially on the table: say so — "1 of 2 served" beats
                      // a blanket "Being prepared" when the kebab is already here.
                      const lines = (o.items || []).filter((l) => l.status);
                      const served = lines.filter((l) => l.status === 'SERVED').length;
                      const partial =
                        st !== 'CANCELLED' && served > 0 && served < lines.length
                          ? ` · ${t('guest.menu.itemStatus.someServed', {
                            served, total: lines.length,
                          })}`
                          : '';
                      return (
                        <>
                          <div className={`mt-2 text-xs flex items-center gap-1 ${meta.cls}`}>
                            <Icon className="h-3.5 w-3.5" />
                            {t(collectAtCounter && meta.collectKey ? meta.collectKey : meta.key)}
                            {partial}
                            {tableCode && st !== 'CANCELLED'
                              ? ` · ${t('guest.menu.tableSuffix', { code: tableCode })}`
                              : ''}
                          </div>
                          {/* A bare "Cancelled" leaves a guest holding a card
                              with nowhere to go. The same words the pop-up
                              used stay on the order itself, because the pop-up
                              is dismissed and this card is not. */}
                          {st === 'CANCELLED' && (
                            <div className="mt-1 text-xs text-gray-500">
                              {t(session?.table_id
                                ? 'guest.menu.cancelled.staff'
                                : 'guest.menu.cancelled.counter')}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PAY BILL tab ─────────────────────────────────────────────────── */}
        {tab === 'bill' && (
          <div className="pt-6">
            <h2 className="text-[22px] font-semibold text-gray-800 mb-4">Pay bill</h2>
            {placedOrders.length === 0 ? (
              <div className="text-center py-14 text-gray-400">
                <Receipt className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Nothing on the bill yet.</p>
              </div>
            ) : billPaid ? (
              /* ── Paid: thank-you + 5-star rating ─────────────────────── */
              <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
                <h3 className="mt-3 text-lg font-semibold text-gray-900">Bill paid — thank you!</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {formatPrice(billPayable)} settled
                  {billAdvance > 0
                    ? ` (${formatPrice(Math.min(billAdvance, billTotal))} advance deducted)`
                    : ''}. We hope you enjoyed your meal!
                </p>
                <div className="mt-5 pt-4 border-t border-green-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {billRating > 0 ? 'Thanks for your feedback!' : 'How was your experience?'}
                  </p>
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => rateBill(n)} aria-label={`${n} stars`}>
                        <Star
                          className="h-8 w-8 transition"
                          style={{ color: n <= billRating ? '#F5A623' : '#D1D5DB' }}
                          fill={n <= billRating ? '#F5A623' : 'none'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={finishAfterPayment}
                  className="mt-5 w-full py-3.5 rounded-xl text-white text-sm font-semibold transition"
                  style={{ background: ORANGE }}
                >
                  Finish & sign out
                </button>
                <p className="mt-2 text-[11px] text-gray-500">
                  Want more? Sign in again to start a fresh order.
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="space-y-2">
                    {placedOrders.map((o, i) => (
                      <div key={`${o.order_number}-${i}`} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Order {o.order_number || '—'}</span>
                        <span className="text-gray-700">{formatPrice(o.total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <span className="font-medium text-gray-900">Total so far</span>
                    <span className="text-lg font-semibold text-gray-900">{formatPrice(billTotal)}</span>
                  </div>
                  {billAdvance > 0 && (
                    <>
                      <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-emerald-700">Booking advance (paid with your reservation)</span>
                        <span className="font-medium text-emerald-700">− {formatPrice(Math.min(billAdvance, billTotal))}</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                        <span className="font-medium text-gray-900">To pay</span>
                        <span className="text-lg font-semibold text-gray-900">{formatPrice(billPayable)}</span>
                      </div>
                    </>
                  )}
                  <p className="mt-3 text-xs text-gray-400">
                    Taxes and any service charge are added on the final bill.
                  </p>
                </div>

                {/* Pay / split actions */}
                {!splitOpen ? (
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={payFullBill}
                      disabled={paySaving}
                      className="w-full py-3.5 rounded-xl text-white text-sm font-semibold transition disabled:opacity-60"
                      style={{ background: ORANGE }}
                    >
                      {paySaving ? 'Processing…' : `Pay full bill · ${formatPrice(billPayable)}`}
                    </button>
                    <button
                      onClick={openSplit}
                      className="w-full py-3.5 rounded-xl border text-sm font-semibold transition"
                      style={{ borderColor: ORANGE, color: ORANGE }}
                    >
                      Split the bill
                    </button>
                  </div>
                ) : (
                  /* ── Split module ───────────────────────────────────── */
                  <div className="mt-4 rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">Split the bill</h3>
                      <button onClick={() => setSplitOpen(false)} aria-label="Close split">
                        <X className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                    {/* Mode chips */}
                    <div className="flex gap-2 flex-wrap">
                      {[
                        { key: 'pax', label: `By guests (${paxCount})` },
                        { key: 'custom', label: 'Custom number' },
                        { key: 'items', label: 'By items' },
                      ].map((m) => (
                        <button
                          key={m.key}
                          onClick={() => setSplitMode(m.key)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                            splitMode === m.key ? 'text-white' : 'text-gray-600 border-gray-200'
                          }`}
                          style={splitMode === m.key ? { background: ORANGE, borderColor: ORANGE } : {}}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>

                    {/* Even split — by the party size entered at the table */}
                    {splitMode === 'pax' && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600">
                          Split evenly between the {paxCount}{' '}
                          {paxCount === 1 ? 'guest' : 'guests'} at your table:
                        </p>
                        <div className="mt-3 rounded-lg bg-gray-50 p-3 text-center">
                          <span className="text-2xl font-semibold text-gray-900">
                            {formatPrice(billPayable / paxCount)}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">per person</span>
                          {billAdvance > 0 && (
                            <span className="block text-[11px] text-emerald-700 mt-0.5">
                              after − {formatPrice(Math.min(billAdvance, billTotal))} booking advance
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Even split — custom people count */}
                    {splitMode === 'custom' && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600">How many people are splitting?</p>
                        <div className="mt-3 flex items-center justify-center gap-5">
                          <button
                            onClick={() => setSplitCount((n) => Math.max(2, n - 1))}
                            className="h-10 w-10 rounded-xl border flex items-center justify-center text-xl leading-none"
                            style={{ borderColor: ORANGE, color: ORANGE }}
                            aria-label="Fewer people"
                          >−</button>
                          <span className="text-2xl font-semibold text-gray-900 w-10 text-center">{splitCount}</span>
                          <button
                            onClick={() => setSplitCount((n) => Math.min(50, n + 1))}
                            className="h-10 w-10 rounded-xl border flex items-center justify-center text-xl leading-none"
                            style={{ borderColor: ORANGE, color: ORANGE }}
                            aria-label="More people"
                          >+</button>
                        </div>
                        <div className="mt-3 rounded-lg bg-gray-50 p-3 text-center">
                          <span className="text-2xl font-semibold text-gray-900">
                            {formatPrice(billPayable / splitCount)}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">per person</span>
                          {billAdvance > 0 && (
                            <span className="block text-[11px] text-emerald-700 mt-0.5">
                              after − {formatPrice(Math.min(billAdvance, billTotal))} booking advance
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* By items — assign each dish to a person (or share it) */}
                    {splitMode === 'items' && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">People</p>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setItemPeople((n) => Math.max(2, n - 1))}
                              className="h-8 w-8 rounded-lg border flex items-center justify-center leading-none"
                              style={{ borderColor: ORANGE, color: ORANGE }}
                              aria-label="Fewer people"
                            >−</button>
                            <span className="font-semibold text-gray-900 w-6 text-center">{itemPeople}</span>
                            <button
                              onClick={() => setItemPeople((n) => Math.min(12, n + 1))}
                              className="h-8 w-8 rounded-lg border flex items-center justify-center leading-none"
                              style={{ borderColor: ORANGE, color: ORANGE }}
                              aria-label="More people"
                            >+</button>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                          Tap who had each dish. Anything left Shared splits
                          across the table.
                        </p>
                        <div className="mt-3 space-y-3">
                          {billLines.map((ln) => (
                            <div key={ln.key} className="rounded-lg border border-gray-100 p-2.5">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-800 min-w-0 truncate pr-2">
                                  {ln.name}
                                  {ln.qty > 1 ? ` ×${ln.qty}` : ''}
                                </span>
                                <span className="text-gray-700 shrink-0">{formatPrice(ln.total)}</span>
                              </div>
                              <div className="mt-2 flex gap-1.5 flex-wrap">
                                {['shared', ...Array.from({ length: itemPeople }, (_, i) => i)].map((who) => {
                                  const eaters = (Array.isArray(itemAssign[ln.key]) ? itemAssign[ln.key] : [])
                                    .filter((i) => typeof i === 'number' && i < itemPeople);
                                  const active = who === 'shared'
                                    ? eaters.length === 0
                                    : eaters.includes(who);
                                  return (
                                    <button
                                      key={String(who)}
                                      onClick={() => toggleItemPerson(ln.key, who)}
                                      className={`px-2.5 py-1 rounded-full border text-[11px] font-medium transition ${
                                        active ? 'text-white' : 'text-gray-500 border-gray-200'
                                      }`}
                                      style={active ? { background: ORANGE, borderColor: ORANGE } : {}}
                                    >
                                      {who === 'shared' ? 'Shared' : `P${who + 1}`}
                                    </button>
                                  );
                                })}
                              </div>
                              {(() => {
                                const eaters = (Array.isArray(itemAssign[ln.key]) ? itemAssign[ln.key] : [])
                                  .filter((i) => typeof i === 'number' && i < itemPeople);
                                if (eaters.length < 2) return null;
                                return (
                                  <p className="mt-1.5 text-[11px] text-gray-400">
                                    Split {eaters.length} ways — {formatPrice(ln.total / eaters.length)} each
                                  </p>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                        {/* Per-person outcome */}
                        <div className="mt-3 rounded-lg bg-gray-50 p-3 space-y-1">
                          {itemSplitTotals.map((t, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">Person {i + 1}</span>
                              <span className="font-semibold text-gray-900">{formatPrice(t)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={payFullBill}
                      disabled={paySaving}
                      className="mt-4 w-full py-3.5 rounded-xl text-white text-sm font-semibold transition disabled:opacity-60"
                      style={{ background: ORANGE }}
                    >
                      {paySaving ? 'Processing…' : `Pay ${formatPrice(billPayable)}`}
                    </button>
                    <p className="mt-2 text-[11px] text-gray-400 text-center">
                      Everyone pays their share to one phone.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Join our rewards — only when the programme's sign-ups are open.
            Writes a PENDING lead; staff confirm it at the counter. */}
        {!loading && context?.loyalty_join_open && (
          <JoinRewards
            programName={context?.loyalty_program_name}
            locationId={context?.location_id}
            accent={ORANGE}
          />
        )}

        {/* Footer — branch identity, like the SOCIAL page footer */}
        {!loading && (
          <footer className="mt-12 mb-4 text-center">
            <div className="text-lg font-bold tracking-wide" style={{ color: ORANGE }}>
              {(menu?.restaurant_name || branchName || 'RestoBOXX').toUpperCase()}
            </div>
            {context?.location_name && (
              <div className="mt-1 text-xs text-gray-400">{context.location_name}</div>
            )}
            <div className="mt-4 text-xs text-gray-400">
              Powered by <span className="font-semibold text-gray-500">RestoBOXX</span>
            </div>
          </footer>
        )}
      </main>

      {/* ── Sticky CONFIRM ORDER bar + bottom nav (pinned to the phone column) ─ */}
      <div className={`fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[440px] z-30 bg-white border-t border-gray-100 ${NAV_WIDE}`}>
        {cartCount > 0 && (
          <div className="max-w-2xl mx-auto px-3 pt-2">
            <button
              onClick={() => setCartOpen(true)}
              className="w-full py-3 px-4 rounded-2xl text-white shadow-lg transition active:scale-[0.99] flex items-center gap-3"
              style={{ background: MAROON }}
            >
              <span className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center font-bold shrink-0">
                {cartCount}
              </span>
              <span className="text-left leading-tight min-w-0">
                <span className="block text-xs" style={{ color: '#F3B48C' }}>Your order</span>
                <span className="block font-semibold">{formatPrice(cartTotal)}</span>
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-lg font-semibold shrink-0">
                Review &amp; place
                <ArrowLeft className="h-5 w-5 rotate-180" />
              </span>
            </button>
          </div>
        )}
        {/* Self-service pays at order time, so there is no Pay Bill tab and no
            waiter to call. Just browsing has no orders and no bill either —
            only the Menu tab (and the phone, which is information). */}
        <nav
          className={`max-w-2xl mx-auto grid py-2 ${
            browseOnly
              ? (context?.restaurant_phone ? 'grid-cols-2' : 'grid-cols-1')
              : isSelfService
                ? 'grid-cols-2'
                : context?.restaurant_phone
                  ? 'grid-cols-4'
                  : 'grid-cols-3'
          }`}
        >
          {[
            { id: 'menu', label: 'Menu', Icon: BookOpen },
            { id: 'orders', label: 'Orders', Icon: ConciergeBell },
            { id: 'bill', label: 'Pay Bill', Icon: Receipt },
          ]
            .filter(({ id }) => !(isSelfService && id === 'bill'))
            .filter(({ id }) => !(browseOnly && id !== 'menu'))
            .map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => { setTab(id); window.scrollTo?.(0, 0); }}
              className={`flex items-center justify-center gap-1.5 py-2 text-[15px] ${
                tab === id ? 'font-semibold' : 'text-gray-700'
              }`}
              style={tab === id ? { color: ORANGE } : {}}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
          {/* Direct action, not a tab — dials the restaurant. */}
          {context?.restaurant_phone && !isSelfService && (
            <a
              href={`tel:${context.restaurant_phone}`}
              className="flex items-center justify-center gap-1.5 py-2 text-[15px] text-gray-700"
            >
              <Phone className="h-5 w-5" />
              Waiter
            </a>
          )}
        </nav>
      </div>

      {/* Customisation sheet — pick a size, then its add-ons */}
      {picker && (() => {
        const { item } = picker;
        const variants = item.variants || [];
        const v = variants.find((x) => x.id === picker.variantId) || variants[0];
        const addons = v?.addons || [];
        const pairings = pairingsFor(item);
        const chosen = addons
          .map((a) => ({ ...a, qty: Number(picker.addons[a.addon_id]) || 0 }))
          .filter((a) => a.qty > 0);
        const total =
          (v?.is_free ? 0 : Number(v?.price) || 0) +
          chosen.reduce((s, a) => s + (Number(a.price) || 0) * a.qty, 0);
        const diet = dietBadge(item);
        return (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col">
              <div className="p-5 pb-3 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
                <div className="min-w-0">
                  <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${diet.cls}`}>
                    {diet.label}
                  </span>
                  <div className="font-semibold text-gray-900 mt-1">{item.name}</div>
                  {item.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  )}
                  {item.spicy_level && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                      <span aria-hidden="true">
                        {'🌶'.repeat(SPICY_PEPPERS[item.spicy_level] || 1)}
                      </span>
                      {t(SPICY_LABELS[item.spicy_level] || 'guest.menu.spicy.normal')}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setPicker(null)}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 pt-3 overflow-y-auto space-y-4">
                {/* Variant pills; picking one reveals its add-ons below */}
                {variants.length > 1 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      Choose an option
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {variants.map((x) => (
                        <button
                          key={x.id}
                          type="button"
                          onClick={() =>
                            setPicker((p) => ({ ...p, variantId: x.id, addons: {} }))
                          }
                          className={`px-4 py-2 rounded-full border text-sm font-medium transition ${
                            x.id === picker.variantId
                              ? 'text-white shadow-sm'
                              : 'bg-white border-gray-200 text-gray-700'
                          }`}
                          style={x.id === picker.variantId ? { background: ORANGE, borderColor: ORANGE } : {}}
                        >
                          {variantLabel(x)}
                          <span className={`ml-1.5 font-semibold ${
                            x.id === picker.variantId ? 'text-orange-100' : ''
                          }`}
                          style={x.id === picker.variantId ? {} : { color: ORANGE }}>
                            {x.is_free ? 'Free' : formatPrice(x.price)}
                          </span>
                        </button>
                      ))}
                    </div>
                    {v && ((v.calories != null && v.calories !== 0) || v.serves > 1) && (
                      <p className="text-[11px] text-gray-400 mt-2">
                        {v.calories != null && v.calories !== 0 ? `${v.calories} kcal` : ''}
                        {v.calories != null && v.calories !== 0 && v.serves > 1 ? ' · ' : ''}
                        {v.serves > 1 ? `serves ${v.serves}` : ''}
                      </p>
                    )}
                  </div>
                )}

                {addons.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      Add-ons <span className="normal-case font-normal">(optional)</span>
                    </div>
                    <div className="space-y-2">
                      {addons.map((a) => (
                        <label
                          key={a.addon_id}
                          className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:border-orange-300 transition"
                        >
                          <span className="flex items-center gap-2.5 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={(picker.addons[a.addon_id] || 0) > 0}
                              onChange={() =>
                                setPicker((p) => ({
                                  ...p,
                                  addons: {
                                    ...p.addons,
                                    [a.addon_id]: (p.addons[a.addon_id] || 0) > 0 ? 0 : 1,
                                  },
                                }))
                              }
                              className="h-4 w-4"
                              style={{ accentColor: ORANGE }}
                            />
                            {a.name}
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            {/* Once ticked, HOW MANY: 2 × Dal Makhani is a stepper, not a second line. */}
                            {(picker.addons[a.addon_id] || 0) > 0 && (
                              <span className="flex items-center gap-1.5"
                                onClick={(e) => e.preventDefault()}>
                                <button type="button" aria-label={`One less ${a.name}`}
                                  onClick={() => setPicker((p) => ({
                                    ...p,
                                    addons: { ...p.addons, [a.addon_id]: Math.max(0, (p.addons[a.addon_id] || 0) - 1) },
                                  }))}
                                  className="h-6 w-6 rounded-md border border-gray-200 text-sm leading-none text-gray-600 hover:border-orange-300">−</button>
                                <span className="w-4 text-center text-sm font-semibold text-gray-900 tabular-nums">
                                  {picker.addons[a.addon_id]}
                                </span>
                                <button type="button" aria-label={`One more ${a.name}`}
                                  onClick={() => setPicker((p) => ({
                                    ...p,
                                    addons: { ...p.addons, [a.addon_id]: Math.min(9, (p.addons[a.addon_id] || 0) + 1) },
                                  }))}
                                  className="h-6 w-6 rounded-md border border-gray-200 text-sm leading-none text-gray-600 hover:border-orange-300">+</button>
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {(picker.addons[a.addon_id] || 0) > 1
                                ? `+ ${formatPrice((Number(a.price) || 0) * picker.addons[a.addon_id])}`
                                : a.price ? `+ ${formatPrice(a.price)}` : 'Free'}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Allergens — always visible when present */}
                {(item.allergens || []).length > 0 && (
                  <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2.5">
                    <div className="text-xs font-semibold text-red-700 mb-1">⚠ Allergens</div>
                    <div className="flex flex-wrap gap-1">
                      {item.allergens.map((al) => (
                        <span
                          key={al}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-red-200 text-red-700"
                        >
                          {al}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ingredients — collapsed by default */}
                {(item.ingredients || []).length > 0 && (
                  <details className="group rounded-lg border border-gray-200 px-3 py-2.5">
                    <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 list-none [&::-webkit-details-marker]:hidden">
                      View ingredients
                      <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.ingredients.map((ing) => (
                        <span
                          key={ing}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                        >
                          {ing}
                        </span>
                      ))}
                    </div>
                  </details>
                )}

                {/* What the kitchen pairs this with, for whichever of those
                    are on this menu. Below the reading matter and above the
                    Add button: a suggestion, not a gate. */}
                {pairings.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      {t('guest.picker.pairs')}
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                      {pairings.map((p) => (
                        <button
                          key={p.dish_id}
                          type="button"
                          onClick={() => openDetails(p)}
                          className="flex w-32 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left"
                        >
                          {p.image
                            ? <img src={p.image} alt="" className="h-20 w-full object-cover" />
                            : <div className="h-20 w-full bg-amber-50" />}
                          <span className="px-2 pt-1.5 text-xs font-semibold leading-tight text-gray-900 line-clamp-2">
                            {p.name}
                          </span>
                          <span className="px-2 pb-2 text-[11px] text-gray-500">{pairingPrice(p)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5 pt-3 border-t border-gray-100 shrink-0">
                {/* Just browsing: the sheet is for READING — description,
                    ingredients, allergens, price — so no Add button at all.
                    At a counter the dish is orderable, so the ordinary Add
                    button stands and the sign-in waits for the order itself. */}
                {readOnlyMenu ? (
                  <p className="text-center text-xs text-gray-400 py-2">
                    You're browsing the menu — nothing is ordered from here.
                  </p>
                ) : v ? (
                  <button
                    onClick={() => {
                      addToCart(item, v, picker.addons);
                      setPicker(null);
                    }}
                    className="w-full py-3.5 rounded-xl text-white text-[15px] font-semibold transition"
                    style={{ background: ORANGE }}
                  >
                    Add to order · {total === 0 ? 'Free' : formatPrice(total)}
                  </button>
                ) : (
                  <p className="text-center text-xs text-gray-400 py-2">
                    Price on request — please ask the staff.
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cart drawer — review lines and confirm */}
      {cartOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h3 className="font-semibold text-gray-900">
                Your order
                {tableCode && (
                  <span className="text-xs font-normal text-gray-400 ml-2">
                    Table {tableCode}
                  </span>
                )}
              </h3>
              <button
                onClick={() => setCartOpen(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-3">
              {cart.map((l) => (
                <div key={l.key} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 break-words">{l.name}</div>
                    {l.variant && <div className="text-xs text-gray-500">{l.variant}</div>}
                    {l.addons.map((a) => (
                      <div key={a.addon_id} className="text-xs text-gray-500 flex items-center gap-1.5">
                        <span className="min-w-0">
                          + {(a.qty || 1) > 1 ? `${a.qty} × ` : ''}{a.name}
                          {Number(a.price) > 0 && (
                            <span className="text-gray-400">
                              {' '}({formatPrice((Number(a.price) || 0) * (a.qty || 1))})
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => removeAddon(l.key, a.addon_id)}
                          className="shrink-0 h-4 w-4 rounded-full bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center"
                          aria-label={`Remove ${a.name}`}
                          title={`Remove ${a.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <div className="text-xs font-medium mt-0.5" style={{ color: ORANGE }}>
                      {formatPrice(l.unitPrice)} each
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => changeQty(l.key, -1)}
                      className="h-7 w-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                      aria-label="Decrease"
                    >
                      {l.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                    </button>
                    <span className="text-sm font-medium w-5 text-center">{l.qty}</span>
                    <button
                      onClick={() => changeQty(l.key, 1)}
                      className="h-7 w-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                      aria-label="Increase"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Note to the kitchen — "less spicy", "no onion", … */}
            <div className="mt-3 shrink-0">
              <textarea
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                maxLength={300}
                rows={2}
                placeholder="Add a note for the kitchen (e.g. less spicy, no onion)…"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm resize-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition"
              />
            </div>

            <div className="mt-2 pt-3 border-t border-gray-100 flex items-center justify-between shrink-0">
              <span className="text-sm text-gray-600">Total</span>
              <span className="font-semibold text-gray-900">{formatPrice(cartTotal)}</span>
            </div>

            {/* No surprise at the last step: the sign-in is announced before the button asks for it. */}
            {browseOnly && (
              <p className="mt-3 text-center text-xs text-gray-500 shrink-0">
                {t('guest.menu.browse.signInNote')}
              </p>
            )}

            {orderError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 shrink-0">
                {orderError}
              </div>
            )}

            <button
              onClick={placeOrder}
              disabled={placing || cart.length === 0}
              className="mt-4 w-full py-3.5 rounded-xl disabled:opacity-60 text-white text-[15px] font-semibold tracking-wide transition shrink-0"
              style={{ background: ORANGE }}
            >
              {placing
                ? preorderId ? 'Saving pre-order…'
                  : isSelfService ? 'Processing payment…' : 'Placing order…'
                : preorderId
                  ? 'SAVE PRE-ORDER'
                  : browseOnly
                    ? t('guest.menu.browse.signInAndPlace')
                  // Kiosk mode drops the table requirement — the website order stands alone.
                  : (session?.table_id || canOrderWithoutTable)
                    ? isSelfService
                      ? `PAY ${formatPrice(cartTotal)} & PLACE ORDER`
                      : 'CONFIRM ORDER'
                    : 'Select table & confirm'}
            </button>
          </div>
        </div>
      )}

      {/* Mock payment sheet — self-service pays before the order exists.
          Everything here is a demo: the card fields accept anything, the
          wallet options just approve, and no money moves. */}
      {paySheet && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
          {/* The wallet viewfinder makes this the tallest the sheet ever gets,
              so it scrolls rather than pushing Pay off a short phone screen. */}
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-gray-900 text-lg">Payment</h3>
              {!paySheet.processing && (
                <button onClick={() => setPaySheet(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close payment">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mb-4">Demo payment — no money moves.</p>
            <div className="rounded-xl px-4 py-3 mb-4 text-center" style={{ background: '#FBEEE4' }}>
              <div className="text-xs text-gray-500">Total to pay</div>
              <div className="text-2xl font-bold" style={{ color: ORANGE }}>{formatPrice(paySheet.amount)}</div>
            </div>
            <div className="flex gap-2 mb-4">
              {[['card', '💳 Card'], ['juice', '📱 Juice'], ['myt', '🏦 my.t money']].map(([m, label]) => (
                <button key={m} type="button" disabled={paySheet.processing}
                  onClick={() => setPaySheet((s) => (s ? { ...s, method: m } : s))}
                  className={`flex-1 rounded-lg border px-2 py-2 text-sm font-medium transition ${
                    paySheet.method === m ? 'border-transparent text-white' : 'border-gray-200 text-gray-600'}`}
                  style={paySheet.method === m ? { background: ORANGE } : {}}>
                  {label}
                </button>
              ))}
            </div>
            {paySheet.method === 'card' ? (
              <div className="space-y-2 mb-4">
                <input type="text" inputMode="numeric" placeholder="4242 4242 4242 4242" disabled={paySheet.processing}
                  aria-label="Card number (demo)"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-300 outline-none focus:border-gray-300" />
                <div className="flex gap-2">
                  <input type="text" inputMode="numeric" placeholder="MM / YY" disabled={paySheet.processing}
                    aria-label="Expiry (demo)"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-300 outline-none focus:border-gray-300" />
                  <input type="password" inputMode="numeric" placeholder="CVV" maxLength={4} disabled={paySheet.processing}
                    aria-label="CVV (demo)"
                    className="w-24 px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-300 outline-none focus:border-gray-300" />
                </div>
              </div>
            ) : canOrderWithoutTable ? (
              /* A kiosk branch HAS a counter, and a counter has the wallet's QR
                 sticker on it — so the guest pays the way they really would,
                 by pointing the phone at it. Still the demo: any QR approves,
                 and where the camera cannot run the Pay button below is still
                 the way through. */
              <WalletQrScanner
                wallet={WALLET_NAMES[paySheet.method] || ''}
                amount={formatPrice(paySheet.amount)}
                onScanned={confirmMockPayment}
              />
            ) : (
              <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-gray-500">
                You&apos;d approve this in the app — in the demo, Pay simply approves it.
              </p>
            )}
            <button type="button" onClick={confirmMockPayment} disabled={paySheet.processing}
              className="w-full py-3.5 rounded-xl text-white text-[15px] font-semibold tracking-wide disabled:opacity-70"
              style={{ background: ORANGE }}>
              {paySheet.processing ? 'Processing payment…' : `Pay ${formatPrice(paySheet.amount)}`}
            </button>
            {!paySheet.processing && (
              <button type="button" onClick={() => setPaySheet(null)}
                className="mt-2 w-full py-2 text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table picker modal */}
      {showTablePicker && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Select your table</h3>
              <button
                onClick={() => setShowTablePicker(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {tableError && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                {tableError}
              </div>
            )}

            {tablesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
                     style={{ borderColor: ORANGE }} />
              </div>
            ) : tables.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No tables are available to select. Please ask the staff.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {tables.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => selectTable(t)}
                    className={`rounded-lg border px-2 py-3 text-center transition ${
                      session?.table_id === t.id
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50/50'
                    }`}
                  >
                    <Armchair className="h-4 w-4 mx-auto mb-1" style={{ color: ORANGE }} />
                    <div className="text-sm font-medium text-gray-900">
                      {t.table_code}
                    </div>
                    {t.capacity ? (
                      <div className="text-[10px] text-gray-400">
                        seats {t.capacity}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* The order-placed moment, straight after a counter payment. It sits
          over everything except the two notices below — a plated dish or a
          pulled order still outranks a celebration. */}
      {celebration && (
        <OrderPlacedCelebration
          orderNumber={celebration.orderNumber}
          items={celebration.items}
          total={celebration.total}
          paid
          etaMinutes={celebration.etaMinutes}
          /* Where the food actually turns up: a counter guest collects it at
             the branch, a seated one has it brought to their table. */
          pickupLabel={
            tableCode
              ? t('guest.menu.success.servedAt')
              : t('guest.menu.success.pickUpAt')
          }
          pickupValue={
            tableCode ? t('guest.menu.tableSuffix', { code: tableCode }) : branchName
          }
          formatPrice={formatPrice}
          onTrack={() => { setCelebration(null); setTab('orders'); }}
          onAddMore={() => { setCelebration(null); setTab('menu'); }}
        />
      )}

      {/* The two facts worth interrupting a guest for: the kitchen has plated
          something, or their order has been pulled. Both open over whatever tab
          they were on rather than waiting to be found on the Orders list. */}
      {notice && (() => {
        const gone = notice.kind === 'cancelled';
        return (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
              <div
                className={`mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-3 ${
                  gone ? 'bg-red-100 text-red-600' : 'bg-orange-100'
                }`}
                style={gone ? undefined : { color: ORANGE }}
              >
                {gone ? <Ban className="h-7 w-7" /> : <ConciergeBell className="h-7 w-7" />}
              </div>
              <h3 className="font-semibold text-gray-900 text-lg">
                {gone
                  ? t('guest.menu.cancelled.title')
                  : notice.whole
                    ? t('guest.menu.ready.orderTitle')
                    : t(notice.dishes.length === 1
                      ? 'guest.menu.ready.dishTitleOne'
                      : 'guest.menu.ready.dishTitleMany')}
              </h3>
              {!gone && (
                <p className="mt-2 text-sm font-medium text-gray-800">
                  {notice.dishes.filter(Boolean).join(' · ')}
                </p>
              )}
              <p className={`text-sm text-gray-500 ${gone ? 'mt-2' : 'mt-1'}`}>
                {gone
                  /* Where to turn depends on where they are: a counter guest has
                     a till to walk to, a seated one has staff to call over. */
                  ? t(session?.table_id
                    ? 'guest.menu.cancelled.staff'
                    : 'guest.menu.cancelled.counter')
                  : collectAtCounter
                    ? t('guest.menu.ready.collect')
                    : tableCode
                      ? t('guest.menu.ready.onItsWayTable', { code: tableCode })
                      : t('guest.menu.ready.onItsWay')}
              </p>
              {notice.orderNumber && (
                <p className="mt-1 text-xs text-gray-400">
                  {t('guest.menu.notice.orderLine', { number: notice.orderNumber })}
                </p>
              )}
              {gone && context?.restaurant_phone && (
                <a
                  href={`tel:${context.restaurant_phone}`}
                  className="mt-5 w-full py-3 rounded-xl border text-sm font-semibold transition inline-flex items-center justify-center gap-2"
                  style={{ borderColor: ORANGE, color: ORANGE }}
                >
                  <Phone className="h-4 w-4" /> {t('guest.menu.callWaiter')}
                </a>
              )}
              {!gone && (
                <button
                  onClick={() => { setNotice(null); setTab('orders'); }}
                  className="mt-5 w-full py-3 rounded-xl text-white text-sm font-semibold transition"
                  style={{ background: ORANGE }}
                >
                  {t('guest.menu.ready.view')}
                </button>
              )}
              <button
                onClick={() => setNotice(null)}
                className={`w-full py-3 rounded-xl text-sm font-semibold transition ${
                  gone ? 'mt-2 text-white' : 'mt-2 border'
                }`}
                style={gone ? { background: ORANGE } : { borderColor: ORANGE, color: ORANGE }}
              >
                {t('guest.menu.notice.dismiss')}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Party-size prompt — must be answered before ordering at a known table */}
      {paxModal && !tableBlocked && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <h3 className="font-semibold text-gray-900 text-lg">How many are dining?</h3>
            <p className="text-sm text-gray-500 mt-1">
              We'll reserve
              {tableCode ? ` Table ${tableCode}` : ' your table'} for your party.
            </p>

            <div className="mt-5 flex items-center justify-center gap-5">
              <button
                onClick={() => setPaxValue((n) => Math.max(1, n - 1))}
                className="h-11 w-11 rounded-xl border flex items-center justify-center text-2xl leading-none"
                style={{ borderColor: ORANGE, color: ORANGE }}
                aria-label="Fewer"
              >
                −
              </button>
              <span className="text-3xl font-semibold text-gray-900 w-12 text-center">{paxValue}</span>
              <button
                onClick={() => setPaxValue((n) => Math.min(50, n + 1))}
                className="h-11 w-11 rounded-xl border flex items-center justify-center text-2xl leading-none"
                style={{ borderColor: ORANGE, color: ORANGE }}
                aria-label="More"
              >
                +
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              {paxValue} {paxValue === 1 ? 'guest' : 'guests'}
            </p>

            {paxError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                {paxError}
              </div>
            )}

            <button
              onClick={reserveWithPax}
              disabled={paxSaving}
              className="mt-5 w-full py-3.5 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition"
              style={{ background: ORANGE }}
            >
              {paxSaving ? 'Reserving…' : 'Confirm & start ordering'}
            </button>
          </div>
        </div>
      )}

      {/* The guest's own sitting is still open at another table — one table at a time */}
      {seatedElsewhere && !tableBlocked && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
            <div
              className="mx-auto h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center mb-3"
              style={{ color: ORANGE }}
            >
              <Armchair className="h-6 w-6" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">
              You're already seated at Table {seatedElsewhere.table_code}
            </h3>
            <p className="text-sm text-gray-600 mt-2">
              Your sitting
              {seatedElsewhere.location_name ? ` at ${seatedElsewhere.location_name}` : ''} is still
              open
              {seatedElsewhere.active_order_number
                ? ` with order ${seatedElsewhere.active_order_number}`
                : ''}
              . Head back to it to keep ordering, or ask the staff to move you to this table.
            </p>
            <button
              onClick={goToMySitting}
              className="mt-5 w-full py-3 rounded-xl text-white text-sm font-semibold transition"
              style={{ background: ORANGE }}
            >
              Go to Table {seatedElsewhere.table_code}
            </button>
            {(blockedPhone || (!isSelfService && context?.restaurant_phone)) && (
              <a
                href={`tel:${blockedPhone || context?.restaurant_phone}`}
                className="mt-3 w-full py-3 rounded-xl border text-sm font-semibold transition inline-flex items-center justify-center gap-2"
                style={{ borderColor: ORANGE, color: ORANGE }}
              >
                Call waiter
              </a>
            )}
          </div>
        </div>
      )}

      {/* Table occupied by another guest — hard block */}
      {tableBlocked && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-3">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h3 className="font-semibold text-gray-900 text-lg">Table not available</h3>
            <p className="text-sm text-gray-600 mt-2">{tableBlocked}</p>
            {blockedPhone && (
              <a
                href={`tel:${blockedPhone}`}
                className="mt-5 w-full py-3 rounded-xl text-white text-sm font-semibold transition inline-flex items-center justify-center gap-2"
                style={{ background: ORANGE }}
              >
                <Phone className="h-4 w-4" /> Call waiter
              </a>
            )}
            <button
              onClick={() => {
                // Sign the guest out so the device is ready for the next scan; the QR token stays in the URL.
                sessionStorage.removeItem('customerToken');
                sessionStorage.removeItem('customerSession');
                sessionStorage.removeItem('customerPlacedOrders');
                sessionStorage.removeItem('customerActiveOrder');
                customerAuth.clear();
                const qrToken = params.get('token');
                navigate(
                  qrToken
                    ? `/customer/login?token=${encodeURIComponent(qrToken)}`
                    : '/customer/account-login',
                  { replace: true }
                );
              }}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition ${
                blockedPhone ? 'mt-2 border' : 'mt-5 text-white'
              }`}
              style={blockedPhone ? { borderColor: ORANGE, color: ORANGE } : { background: ORANGE }}
            >
              Log out
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
